/**
 * AI Recruit - Resume Detail Page
 * Comprehensive AI analysis view for individual resumes
 */

import React, { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/hooks/useAuth'
import { usePermission } from '@/hooks/usePermission'
import { toDisplayPercent } from '@/components/talentos'
import TalentosResumeProfileTab from '@/components/resume/TalentosResumeProfileTab'
import { formatToLocalDate, formatToLocalDateTime } from '../../utils/dateUtils'
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  UserIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  StarIcon,
  ChartBarIcon,
  ClockIcon,
  MapPinIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CpuChipIcon,
  EyeIcon,
  BeakerIcon,
  CodeBracketIcon,
  DocumentDuplicateIcon,
  ShieldExclamationIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  CogIcon,
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  LightBulbIcon,
  NoSymbolIcon,
  InformationCircleIcon,
  MicrophoneIcon,
  TagIcon
} from '@heroicons/react/24/outline'

import { resumesApi, matchingApi, resumeGenerationApi, jobsApi } from '@/services/api'
import { interviewApi } from '@/services/interviewApi'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import MarkdownRenderer from '@/components/common/MarkdownRenderer'
import Tooltip from '@/components/common/Tooltip'
import CollapsibleCard from '@/components/common/CollapsibleCard'
import { downloadResumeAsWord } from '@/utils/downloadResumeAsWord'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import InterviewModal from '@/components/InterviewModal'
import InterviewTab from '@/components/interview/InterviewTab'
import AIInterviewButton from '@/components/interview/AIInterviewButton'
import { getInitials, getColor } from '@/components/jobs/AssignedRecruitersDisplay'
import { getStageColor } from '@/utils/stageColors'
import ApplicationStatusDropdown from '@/components/matching/ApplicationStatusDropdown'
import MatchingStatusBadge from '@/components/matching/MatchingStatusBadge'
import { FilterPanel, ResultCountDisplay } from '@/components/matching'
import { useMatchFilters } from '@/hooks/useMatchFilters'
import {
  AssignmentBadge,
  EditAssignmentButton,
  ViewAuditTrailButton,
  AssignmentEditPopover,
  AuditTrailModal,
} from '@/components/assignment'

// Badge colors for the job's own posting status (JobStatus values) in the
// Job Matching applications table
const JOB_STATUS_BADGE_CLASSES: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  draft: 'bg-blue-100 text-blue-800',
  paused: 'bg-yellow-100 text-yellow-800',
  inactive: 'bg-gray-100 text-gray-700',
  closed: 'bg-red-100 text-red-800',
}

// Legacy application_status code -> label, same format ApplicationStatusDropdown
// uses for its "(old)" status ("L1_REJECTED" -> "L1 Rejected")
const prettifyStatusCode = (code: string) =>
  code.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')

const formatShortDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

// Session status -> label. A proctored session is auto-created (status
// "pending") for every applicant once the JD has an active proctored config,
// so "pending" with no invite sent means it was never scheduled.
const getInterviewStatusLabel = (kind: 'proctored' | 'live', session: any): string => {
  if (kind === 'proctored' && session.status === 'pending') {
    return session.invite_sent_at ? 'Invited' : 'Not scheduled'
  }
  const labels: Record<string, string> = {
    verified: 'Verified',
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    completed: 'Completed',
  }
  return labels[session.status] || session.status
}

// Fixed label width so the Proctored/Live values line up under each other
// (the "Invited" line's pl-[4.375rem] = this w-16 + the row's space-x-1.5 gap)
const INTERVIEW_LABEL_CLASS = 'w-16 flex-shrink-0 font-medium text-gray-500'

// One interview type's line in the Job Matching table: status or score, the
// invite date, and an (i) with the full invite/start/completion timestamps.
const InterviewStatusLine = ({ label, session, kind }: { label: string; session: any; kind: 'proctored' | 'live' }) => {
  if (!session) {
    return (
      <div className="flex items-center space-x-1.5 text-xs text-gray-400">
        <span className={INTERVIEW_LABEL_CLASS}>{label}:</span>
        <span>Not scheduled</span>
      </div>
    )
  }
  const hasScore = session.score !== null && session.score !== undefined
  const timestamps: [string, string | null][] = [
    ['Invite sent', session.invite_sent_at],
    ['Started', session.started_at],
    ['Completed', session.completed_at],
  ]
  const shownTimestamps = timestamps.filter(([, value]) => !!value)
  return (
    <div className="text-xs">
      <div className="flex items-center space-x-1.5">
        <span className={INTERVIEW_LABEL_CLASS}>{label}:</span>
        {hasScore ? (
          <span className={`font-semibold ${
            session.score >= 70 ? 'text-green-600' :
            session.score >= 50 ? 'text-yellow-600' :
            'text-red-600'
          }`}>
            Score {Math.round(session.score)}
          </span>
        ) : (
          <span className="text-gray-700">{getInterviewStatusLabel(kind, session)}</span>
        )}
        {shownTimestamps.length > 0 && (
          <Tooltip
            content={
              <div className="space-y-0.5">
                {shownTimestamps.map(([name, value]) => (
                  <div key={name}>
                    <span className="text-gray-500">{name}:</span> {formatToLocalDateTime(value)}
                  </div>
                ))}
              </div>
            }
            position="bottom"
            theme="light"
            width="max-content"
          >
            <InformationCircleIcon className="w-3.5 h-3.5 text-gray-400 hover:text-blue-600 cursor-help" />
          </Tooltip>
        )}
      </div>
      {session.invite_sent_at && (
        <div className="text-gray-500 pl-[4.375rem]">Invited {formatShortDate(session.invite_sent_at)}</div>
      )}
    </div>
  )
}

const formatPhoneNumber = (phone: string): string => {
  if (!phone) return ''

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')
  
  // Format based on length and country code pattern
  if (digits.length === 10) {
    // 10 digits - US without country code: (234) 567-8901
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  } 
  else if (digits.length === 11 && digits.startsWith('1')) {
    // 11 digits starting with 1 - US with country code: +1 (234) 567-8901
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  } 
  else if (digits.length === 12 && digits.startsWith('91')) {
    // 12 digits starting with 91 - Indian with country code: +91 12345 67890
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`
  }
  else if (digits.length === 11 && digits.startsWith('91')) {
    // 11 digits starting with 91
    return `+91 ${digits.slice(2)}`
  }
  else if (digits.length > 10) {
    // Generic international format
    if (digits.startsWith('91')) {
      return `+91 ${digits.slice(2)}`
    } else if (digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
    } else {
      return `+${digits.slice(0, 2)} ${digits.slice(2)}`
    }
  } 
  else {
    return phone
  }
}


// Résumé Profile redesign (2026-10-01): a TalentOS-analysed résumé
// (resume.analysis_engine === 'talentos') shows a single "Resume Profile"
// tab (TalentosResumeProfileTab) sourced entirely from TalentOS data,
// replacing the old Overview/Skills Analysis/Experience/Education tabs
// (which depended on Foundry-only fields -- the "Quality Assessment" scores
// and the "agents" tab's agent_*_markdown cards -- that TalentOS never
// populates). Old Foundry-analysed résumés are completely unaffected: they
// keep exactly what they show today. First piece built: the main résumé
// detail screen. Flip back to false only if this needs to be rolled back.
const SHOW_TALENTOS_RESUME_UI = true

interface ResumeAnalysis {
  id: string
  candidate_id: string
  status: string
  blacklist?: boolean
  source_id?: string
  source_name?: string
  source_is_active?: boolean
  // Which engine produced this résumé's current analysis ("foundry" or
  // "talentos") -- see SHOW_TALENTOS_RESUME_UI below for how this is meant
  // to be used once that UI work lands.
  analysis_engine?: string
  // Agent markdown outputs
  agent_personal_info_markdown?: string
  agent_experience_markdown?: string
  agent_skills_markdown?: string
  agent_education_markdown?: string
  agent_quality_markdown?: string
  agent_reasoning_markdown?: string
  agent_raw_outputs?: Record<string, any>
  // AI analysis data (from backend 'analysis' field)
  analysis?: {
    personal_info?: {
      full_name?: string
      email?: string
      phone?: string
      location?: string
      linkedin_url?: string
      github_url?: string
      portfolio_url?: string
      professional_title?: string
    }
    career_analysis?: {
      total_years_experience?: number
      current_position?: string
      current_company?: string
      leadership_experience?: boolean
      career_progression_score?: number
      work_experience?: Array<{
        company: string
        position: string
        start_date: string
        end_date: string
        description: string
        achievements: string[]
      }>
    }
    skills_analysis?: {
      technical_skills?: string[]
      soft_skills?: string[]
      programming_languages?: string[]
      frameworks_libraries?: string[]
      tools_technologies?: string[]
      certifications?: string[]
      skill_categories?: Record<string, string[]>
      skill_proficiency?: Record<string, string>
      detailed_table?: Record<string, Array<{
        skill: string
        proficiency?: string
        years_experience?: string
        context_demonstrated?: string
        frequency_of_use?: string
        evidence_of_expertise?: string
        core_or_supporting?: string
        current_or_outdated?: string
        emerging?: boolean
      }>>
    }
    education_analysis?: {
      education?: Array<{
        degree: string
        institution: string
        graduation_date: string
        gpa?: string
        honors?: string
      }>
      highest_degree?: string
      relevant_education?: boolean
    }
    quality_assessment?: {
      overall_score?: number
      quality_scores?: {
        completeness?: number
        relevance?: number
        presentation?: number
        achievements?: number
      }
      completeness_score?: number  // Keep for backward compatibility
      relevance_score?: number     // Keep for backward compatibility
      presentation_score?: number  // Keep for backward compatibility
      strengths?: string[]
      areas_for_improvement?: string[]
      recommendations?: string[]
    }
  }
  quality_score?: number
  confidence_score?: number
  processing_time?: number
  error_message?: string
  // Duplicate detection fields
  is_duplicate?: boolean
  duplicate_confidence?: number
  duplicate_matches?: Array<{
    resume_id: string
    candidate_name?: string
    match_type: string
    confidence: number
    filename: string
  }>
  duplicate_action?: string
  duplicate_reasoning?: string
  job_matching?: {
    matches?: Array<{
      job_id: string
      job_title: string
      overall_score: number
      vector_similarity_score: number
      confidence_level: number
      skills_score: number
      experience_score: number
      education_score: number
      location_score: number
      cultural_fit_score: number
      detailed_reasoning: string
      recommendation: string
      key_strengths: string[]
      key_concerns: string[]
      matched_skills?: Array<{ skill_name: string; match_score: number }>
      missing_skills?: Array<{ skill_name: string; match_score: number }>
      success_probability: number
      retention_prediction: string
      interview_focus_areas: string[]
      onboarding_recommendations: string[]
    }>
    total_jobs_evaluated: number
    total_matches_found: number
    confidence: number
    matching_method: string
    search_query: string
    vector_search_metadata?: {
      similarity_threshold: number
      top_k_searched: number
      organization_id: string
    }
  }
  candidate_name?: string
  candidate_phone?: string
  resume_filename?: string
}

interface Job {
  id: string
  job_id?: string
  title: string
  department?: string
  location?: string
  employment_type: string
  remote_option: string
  description: string
  responsibilities?: string
  requirements?: string
  benefits?: string
  salary_min?: number
  salary_max?: number
  salary_currency: string
  application_deadline?: string
  status: string
  processing_status?: string
  created_at: string
  updated_at: string
  ai_analysis?: any
  skills_analysis?: string
  experience_assessment?: string
  interview_questions?: string
  analysis_generated_at?: string
}

// Job Popup Modal Component
const JobPopupModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  jobId: string
}> = ({ isOpen, onClose, jobId }) => {
  const [activeTab, setActiveTab] = useState('overview')
  const [job, setJob] = useState<Job | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch job data
  useEffect(() => {
    if (isOpen && jobId) {
      const fetchJob = async () => {
        try {
          setIsLoading(true)
          setError(null)
          
          // Validate jobId format
          if (!jobId || typeof jobId !== 'string') {
            throw new Error('Invalid job ID provided')
          }
          
          const response = await jobsApi.getById(jobId)
          
          if (!response || !response.data) {
            throw new Error('No job data received from API')
          }
          
          setJob(response.data)
        } catch (err) {
          console.error('Failed to fetch job:', err)
          setError(`Failed to load job details: ${err instanceof Error ? err.message : 'Unknown error'}`)
        } finally {
          setIsLoading(false)
        }
      }
      fetchJob()
    }
  }, [isOpen, jobId])

  const tabs = [
    { id: 'overview', name: 'Overview', icon: EyeIcon },
    { id: 'skills', name: 'Required Skills', icon: CpuChipIcon },
    { id: 'experience', name: 'Experience', icon: ChartBarIcon }
  ]

  const formatSalary = (min?: number, max?: number, currency: string = 'USD') => {
    if (!min && !max) return 'Not specified'
    if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()} ${currency}`
    if (min) return `$${min.toLocaleString()}+ ${currency}`
    if (max) return `Up to $${max.toLocaleString()} ${currency}`
    return 'Not specified'
  }

  const renderFormattedText = (text: string) => {
    if (!text) return <p className="text-gray-500 italic">No information provided</p>
    
    // Split by double line breaks to create paragraphs
    const paragraphs = text.split('\n\n').filter(p => p.trim())
    
    return (
      <div className="space-y-3">
        {paragraphs.map((paragraph, index) => (
          <p key={index} className="text-gray-700 leading-relaxed">
            {paragraph.trim()}
          </p>
        ))}
      </div>
    )
  }

  // Render structured skills analysis data
  const renderStructuredSkillsAnalysis = (content: string) => {
    try {
      const data = JSON.parse(content)
      const elements: React.ReactElement[] = []
      let currentIndex = 0

      Object.entries(data).forEach(([categoryKey, categoryData]: [string, any]) => {
        if (categoryData && typeof categoryData === 'object' && categoryData.title && categoryData.items) {
          elements.push(
            <div key={currentIndex++} className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <CpuChipIcon className="w-5 h-5 text-blue-600 mr-2" />
                {categoryData.title}
              </h4>
              {categoryData.description && (
                <p className="text-sm text-gray-600 mb-4">{categoryData.description}</p>
              )}
              <div className="space-y-3">
                {categoryData.items.map((item: any, itemIndex: number) => (
                  <div key={itemIndex} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h6 className="text-sm font-semibold text-gray-900 mb-2">
                      {item.skill || item.tool || item.competency || 'Item'}
                    </h6>
                    <p className="text-sm text-gray-700 mb-2 leading-relaxed">
                      {item.description}
                    </p>
                    {item.importance && (
                      <p className="text-xs text-gray-600 italic">
                        <span className="font-medium">Importance:</span> {item.importance}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        }
      })

      if (data.supporting_evidence && Array.isArray(data.supporting_evidence)) {
        elements.push(
          <div key={currentIndex++} className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
              Supporting Evidence
            </h4>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <ul className="space-y-2">
                {data.supporting_evidence.map((evidence: string, index: number) => (
                  <li key={index} className="flex items-start text-sm text-green-800">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="leading-relaxed">{evidence}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      }

      if (data.suggestions && Array.isArray(data.suggestions)) {
        elements.push(
          <div key={currentIndex++} className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <LightBulbIcon className="w-5 h-5 text-yellow-600 mr-2" />
              Suggestions
            </h4>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <ul className="space-y-2">
                {data.suggestions.map((suggestion: string, index: number) => (
                  <li key={index} className="flex items-start text-sm text-yellow-800">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="leading-relaxed">{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      }

      return <div className="space-y-4">{elements}</div>
    } catch (error) {
      console.error('Error parsing structured skills analysis:', error)
      return formatResponseContent(content)
    }
  }

  // Render structured experience assessment data
  const renderStructuredExperienceAssessment = (content: string) => {
    try {
      const data = JSON.parse(content)
      const elements: React.ReactElement[] = []
      let currentIndex = 0

      Object.entries(data).forEach(([categoryKey, categoryData]: [string, any]) => {
        if (categoryData && typeof categoryData === 'object' && categoryData.title && categoryData.items) {
          elements.push(
            <div key={currentIndex++} className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <ChartBarIcon className="w-5 h-5 text-purple-600 mr-2" />
                {categoryData.title}
              </h4>
              {categoryData.description && (
                <p className="text-sm text-gray-600 mb-4">{categoryData.description}</p>
              )}
              <div className="space-y-3">
                {categoryData.items.map((item: any, itemIndex: number) => (
                  <div key={itemIndex} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h6 className="text-sm font-semibold text-gray-900 mb-2">
                      {item.experience || item.industry || item.leadership || item.project || 'Item'}
                    </h6>
                    <p className="text-sm text-gray-700 mb-2 leading-relaxed">
                      {item.description}
                    </p>
                    {item.importance && (
                      <p className="text-xs text-gray-600 italic">
                        <span className="font-medium">Importance:</span> {item.importance}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        }
      })

      if (data.supporting_evidence && Array.isArray(data.supporting_evidence)) {
        elements.push(
          <div key={currentIndex++} className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
              Supporting Evidence
            </h4>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <ul className="space-y-2">
                {data.supporting_evidence.map((evidence: string, index: number) => (
                  <li key={index} className="flex items-start text-sm text-green-800">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="leading-relaxed">{evidence}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      }

      if (data.suggestions && Array.isArray(data.suggestions)) {
        elements.push(
          <div key={currentIndex++} className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <LightBulbIcon className="w-5 h-5 text-yellow-600 mr-2" />
              Suggestions
            </h4>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <ul className="space-y-2">
                {data.suggestions.map((suggestion: string, index: number) => (
                  <li key={index} className="flex items-start text-sm text-yellow-800">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="leading-relaxed">{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      }

      return <div className="space-y-4">{elements}</div>
    } catch (error) {
      console.error('Error parsing structured experience:', error)
      return formatResponseContent(content)
    }
  }

  // Format response content for better readability
  const formatResponseContent = (content: string) => {
    if (!content) return null
    
    const lines = content.split('\n')
    const elements: React.ReactElement[] = []
    let currentIndex = 0
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      
      if (!line) continue
      
      // Handle headers
      if (line.startsWith('# ')) {
        elements.push(
          <h1 key={currentIndex++} className="text-2xl font-bold text-gray-900 mb-4">
            {line.substring(2)}
          </h1>
        )
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={currentIndex++} className="text-xl font-semibold text-gray-900 mb-3">
            {line.substring(3)}
          </h2>
        )
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={currentIndex++} className="text-lg font-medium text-gray-900 mb-2">
            {line.substring(4)}
          </h3>
        )
      } else if (line.startsWith('#### ')) {
        elements.push(
          <h4 key={currentIndex++} className="text-base font-medium text-gray-900 mb-2">
            {line.substring(5)}
          </h4>
        )
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        // Handle bullet points
        const listItems = [line.substring(2)]
        let j = i + 1
        while (j < lines.length && (lines[j].startsWith('- ') || lines[j].startsWith('* '))) {
          listItems.push(lines[j].substring(2))
          j++
        }
        i = j - 1
        
        elements.push(
          <ul key={currentIndex++} className="list-disc list-inside space-y-1 mb-4">
            {listItems.map((item, index) => (
              <li key={index} className="text-gray-700">{item}</li>
            ))}
          </ul>
        )
      } else if (line.startsWith('1. ') || /^\d+\. /.test(line)) {
        // Handle numbered lists
        const listItems = [line.replace(/^\d+\. /, '')]
        let j = i + 1
        while (j < lines.length && /^\d+\. /.test(lines[j])) {
          listItems.push(lines[j].replace(/^\d+\. /, ''))
          j++
        }
        i = j - 1
        
        elements.push(
          <ol key={currentIndex++} className="list-decimal list-inside space-y-1 mb-4">
            {listItems.map((item, index) => (
              <li key={index} className="text-gray-700">{item}</li>
            ))}
          </ol>
        )
      } else {
        // Regular paragraph
        elements.push(
          <p key={currentIndex++} className="text-gray-700 mb-3 leading-relaxed">
            {line}
          </p>
        )
      }
    }
    
    return <div className="space-y-2">{elements}</div>
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white min-h-[90vh]">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {isLoading ? 'Loading...' : job?.title || 'Job Details'}
              </h3>
              {job && (
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                  <span className="flex items-center">
                    <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                    {job.department || 'No department'}
                  </span>
                  <span className="flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    {job.location || 'No location'}
                  </span>
                  <span className="flex items-center">
                    <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                    {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-600">{error}</p>
            </div>
          ) : job ? (
            <>
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  {tabs.map((tab) => {
                    const Icon = tab.icon
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveTab(tab.id)
                          // Note: This is inside JobPopupModal, no URL persistence here
                        }}
                        className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                        <span>{tab.name}</span>
                      </button>
                    )
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="space-y-6">
                {activeTab === 'overview' && (
                  <>
                    {/* AI Analysis Section - Always Display */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                      <div className="px-6 py-4 border-b border-blue-200">
                        <div className="flex items-center">
                          <CpuChipIcon className="w-6 h-6 text-blue-600 mr-3" />
                          <h2 className="text-xl font-semibold text-gray-900">Analysis Results</h2>
                        </div>
                      </div>

                      <div className="p-6 space-y-6">
                        {/* Extract AI analysis data */}
                        {(() => {
                          const analysis = job.ai_analysis || {}
                          const skills = analysis.skills || []
                          const experienceReq = analysis.experience_requirements || {}
                          const educationReq = analysis.education_requirements || {}
                          const analysisInsights = analysis.analysis_insights || {}

                          return (
                            <>
                              {/* Skills Analysis - Always Display */}
                              <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                  <LightBulbIcon className="w-5 h-5 text-yellow-500 mr-2" />
                                  Skills Analysis
                                </h3>
                                {skills.length > 0 ? (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <h4 className="font-medium text-green-700 mb-2">Required Skills</h4>
                                      <div className="flex flex-wrap gap-2">
                                        {skills.filter((skill: any) => skill.importance === 'required').map((skill: any, index: number) => (
                                          <span key={index} className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                                            {skill.skill}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                    <div>
                                      <h4 className="font-medium text-blue-700 mb-2">Preferred Skills</h4>
                                      <div className="flex flex-wrap gap-2">
                                        {skills.filter((skill: any) => skill.importance === 'preferred').map((skill: any, index: number) => (
                                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                            {skill.skill}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-gray-500 italic">Not Available</p>
                                )}
                              </div>

                              {/* Experience Requirements - Always Display */}
                              <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                  <ChartBarIcon className="w-5 h-5 text-purple-500 mr-2" />
                                  Experience
                                </h3>
                                {(experienceReq.min_years_total || experienceReq.max_years_total) ? (
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                      <span className="text-sm text-gray-600">Total Experience</span>
                                      <p className="font-medium">
                                        {experienceReq.min_years_total && experienceReq.max_years_total 
                                          ? `${experienceReq.min_years_total}-${experienceReq.max_years_total} years`
                                          : experienceReq.min_years_total 
                                          ? `${experienceReq.min_years_total}+ years`
                                          : experienceReq.max_years_total
                                          ? `Up to ${experienceReq.max_years_total} years`
                                          : 'Not specified'
                                        }
                                      </p>
                                    </div>
                                    {experienceReq.leadership_required && (
                                      <div>
                                        <span className="text-sm text-gray-600">Leadership</span>
                                        <p className="font-medium text-orange-600">Required</p>
                                      </div>
                                    )}
                                    {experienceReq.specific_experience_areas?.length > 0 && (
                                      <div>
                                        <span className="text-sm text-gray-600">Specific Areas</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {experienceReq.specific_experience_areas.map((area: string, index: number) => (
                                            <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                                              {area}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-gray-500 italic">Not Available</p>
                                )}
                              </div>

                              {/* Education Requirements - Always Display */}
                              <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                  <AcademicCapIcon className="w-5 h-5 text-indigo-500 mr-2" />
                                  Education Requirements
                                </h3>
                                {(educationReq.degree_level || educationReq.field_of_study?.length > 0) ? (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {educationReq.degree_level && (
                                      <div>
                                        <span className="text-sm text-gray-600">Degree Level</span>
                                        <p className="font-medium capitalize">{educationReq.degree_level}</p>
                                      </div>
                                    )}
                                    {educationReq.field_of_study?.length > 0 && (
                                      <div>
                                        <span className="text-sm text-gray-600">Field of Study</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {educationReq.field_of_study.map((field: string, index: number) => (
                                            <span key={index} className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-sm">
                                              {field}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-gray-500 italic">Not Available</p>
                                )}
                              </div>

                              {/* Quality Assessment - Always Display for Admin */}
                              <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                  <StarIcon className="w-5 h-5 text-amber-500 mr-2" />
                                  Quality Assessment
                                </h3>
                                {(analysisInsights.clarity_score || analysisInsights.completeness_score || analysisInsights.attractiveness_score) ? (
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {analysisInsights.clarity_score && (
                                      <div>
                                        <span className="text-sm text-gray-600">Clarity Score</span>
                                        <div className="flex items-center mt-1">
                                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                            <div 
                                              className="bg-blue-500 h-2 rounded-full" 
                                              style={{ width: `${analysisInsights.clarity_score * 100}%` }}
                                            ></div>
                                          </div>
                                          <span className="text-sm font-medium">{Math.round(analysisInsights.clarity_score * 100)}%</span>
                                        </div>
                                      </div>
                                    )}
                                    {analysisInsights.completeness_score && (
                                      <div>
                                        <span className="text-sm text-gray-600">Completeness</span>
                                        <div className="flex items-center mt-1">
                                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                            <div 
                                              className="bg-green-500 h-2 rounded-full" 
                                              style={{ width: `${analysisInsights.completeness_score * 100}%` }}
                                            ></div>
                                          </div>
                                          <span className="text-sm font-medium">{Math.round(analysisInsights.completeness_score * 100)}%</span>
                                        </div>
                                      </div>
                                    )}
                                    {analysisInsights.attractiveness_score && (
                                      <div>
                                        <span className="text-sm text-gray-600">Attractiveness</span>
                                        <div className="flex items-center mt-1">
                                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                            <div 
                                              className="bg-purple-500 h-2 rounded-full" 
                                              style={{ width: `${analysisInsights.attractiveness_score * 100}%` }}
                                            ></div>
                                          </div>
                                          <span className="text-sm font-medium">{Math.round(analysisInsights.attractiveness_score * 100)}%</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-gray-500 italic">Not Available</p>
                                )}
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    </div>

                    {/* Job Details Section */}
                    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                      {/* Job Description */}
                      <div className="px-6 py-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
                        <div className="prose max-w-none text-gray-700">
                          {job.description.split('\n').map((paragraph, index) => (
                            <p key={index} className="mb-3">{paragraph}</p>
                          ))}
                        </div>
                      </div>

                      {/* Responsibilities */}
                      {job.responsibilities && (
                        <div className="px-6 py-6 border-t border-gray-200">
                          <h2 className="text-xl font-semibold text-gray-900 mb-4">Responsibilities</h2>
                          <div className="prose max-w-none text-gray-700">
                            <ul className="space-y-2">
                              {job.responsibilities.split('\n').filter(item => item.trim()).map((item, index) => (
                                <li key={index} className="flex items-start space-x-3">
                                  <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                                  <span className="text-gray-700">{item.trim()}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* Requirements */}
                      {job.requirements && (
                        <div className="px-6 py-6 border-t border-gray-200">
                          <h2 className="text-xl font-semibold text-gray-900 mb-4">Requirements</h2>
                          <div className="prose max-w-none text-gray-700">
                            <ul className="space-y-2">
                              {job.requirements.split('\n').filter(item => item.trim()).map((item, index) => (
                                <li key={index} className="flex items-start space-x-3">
                                  <span className="text-green-500 mt-1 flex-shrink-0">•</span>
                                  <span className="text-gray-700">{item.trim()}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* Benefits */}
                      {job.benefits && (
                        <div className="px-6 py-6 border-t border-gray-200">
                          <h2 className="text-xl font-semibold text-gray-900 mb-4">Benefits</h2>
                          <div className="prose max-w-none text-gray-700">
                            <ul className="space-y-2">
                              {job.benefits.split('\n').filter(item => item.trim()).map((item, index) => (
                                <li key={index} className="flex items-start space-x-3">
                                  <span className="text-purple-500 mt-1 flex-shrink-0">•</span>
                                  <span className="text-gray-700">{item.trim()}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {activeTab === 'skills' && (
                  <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                          <CpuChipIcon className="w-6 h-6 text-blue-600 mr-3" />
                          Skills Analysis
                        </h2>
                      </div>
                    </div>
                    <div className="p-6">
                      {job.skills_analysis && typeof job.skills_analysis === 'string' ? (
                        <div className="max-w-none">
                          {renderStructuredSkillsAnalysis(job.skills_analysis)}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <CpuChipIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No skills analysis available for this job</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'experience' && (
                  <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                          <ChartBarIcon className="w-6 h-6 text-purple-600 mr-3" />
                          Experience Assessment
                        </h2>
                      </div>
                    </div>
                    <div className="p-6">
                      {job.experience_assessment && typeof job.experience_assessment === 'string' ? (
                        <div className="max-w-none">
                          {renderStructuredExperienceAssessment(job.experience_assessment)}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No experience assessment available for this job</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

interface JobMatch {
  job_id: string
  job_title: string
  overall_score: number
  vector_similarity_score: number
  confidence_level: number
  skills_score: number
  experience_score: number
  education_score: number
  location_score: number
  cultural_fit_score: number
  detailed_reasoning: string
  recommendation: string
  key_strengths: string[]
  key_concerns: string[]
  matched_skills?: Array<{ skill_name: string; match_score: number }>
  missing_skills?: Array<{ skill_name: string; match_score: number }>
  success_probability: number
  retention_prediction: string
  interview_focus_areas: string[]
  onboarding_recommendations: string[]
}

const ResumeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('overview')
  const { user } = useAuth()

  // Resolve tab on mount: URL param > default (overview)
  // Note: We no longer persist to localStorage on mount - only when user explicitly clicks a tab
  useEffect(() => {
    const validTabs = ['overview', 'skills', 'experience', 'education', 'agents', 'matching', 'interview'] as const

    const params = new URLSearchParams(window.location.search)
    const urlTab = params.get('tab')
    if (urlTab && (validTabs as readonly string[]).includes(urlTab)) {
      setActiveTab(urlTab as typeof validTabs[number])
    } else {
      // Default to overview if no URL param
      setActiveTab('overview')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Sync when URL params change (back/forward)
  // Note: Don't save to localStorage here - only save when user explicitly clicks a tab
  useEffect(() => {
    const tab = searchParams.get('tab')
    const validTabs = ['overview', 'skills', 'experience', 'education', 'agents', 'matching', 'interview'] as const
    if (tab && (validTabs as readonly string[]).includes(tab)) {
      setActiveTab(tab as typeof validTabs[number])
    }
  }, [searchParams])

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const actionsDropdownRef = useRef<HTMLDivElement>(null)

  // Job matching state
  const [searchTerm, setSearchTerm] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedJobs, setSelectedJobs] = useState<string[]>([])
  const [showComparison, setShowComparison] = useState(false)
  const [comparisonResults, setComparisonResults] = useState<any>(null)
  const [comparisonLoading, setComparisonLoading] = useState(false)
  const [selectedJobForDetails, setSelectedJobForDetails] = useState<string | null>(null)
  const [reprocessingRecords, setReprocessingRecords] = useState<Set<string>>(new Set())
  const [reprocessingJobs, setReprocessingJobs] = useState<Set<string>>(new Set())
  
  // Application status state
  const [applicationStatuses, setApplicationStatuses] = useState<Record<string, string>>({})
  
  // Initialize match filters hook
  const {
    filterState,
    setFilterState,
    filteredMatches,
    uniqueUsers,
    uniqueStatuses,
    clearFilters,
    togglePanel,
    hasActiveFilters: hasFilters,
    activeFilterCount
  } = useMatchFilters(comparisonResults?.job_matches || [])
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredMatches.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedMatches = filteredMatches.slice(startIndex, endIndex)

  // Applications per pipeline stage, for the Job Matching summary
  const stageBreakdown: [string, number][] = (() => {
    const counts: Record<string, number> = {}
    const matches = (comparisonResults && comparisonResults.job_matches) || []
    for (const match of matches) {
      const stageName = match.stage_name || 'No status'
      counts[stageName] = (counts[stageName] || 0) + 1
    }
    return Object.entries(counts)
  })()

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filteredMatches.length])
  
  
  // Interview state
  const [showInterviewModal, setShowInterviewModal] = useState(false)
  const [interviewGuide, setInterviewGuide] = useState<any>(null)
  const [interviewLoading, setInterviewLoading] = useState(false)
  const [interviewReprocessing, setInterviewReprocessing] = useState(false)
  const [selectedJobForInterview, setSelectedJobForInterview] = useState<string | null>(null)
  const [l1Loading, setL1Loading] = useState<string | null>(null)
  const [l2Loading, setL2Loading] = useState<string | null>(null)
  const [showInterviewDropdown, setShowInterviewDropdown] = useState<string | null>(null)
  const [showResumeActionsDropdown, setShowResumeActionsDropdown] = useState(false)
  const [showJobActionsDropdown, setShowJobActionsDropdown] = useState<string | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<'above' | 'below'>('below')
  const [buttonPosition, setButtonPosition] = useState<{ left: number; top: number; bottom: number } | null>(null)
  
  // Job popup state
  const [showJobPopup, setShowJobPopup] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
  
  // Assignment tracking state
  const [showAssignmentEdit, setShowAssignmentEdit] = useState(false)
  const [showAuditTrail, setShowAuditTrail] = useState(false)
  const [selectedAssignment, setSelectedAssignment] = useState<{
    entityType: 'resume_upload' | 'job_match'
    entityId: string
    entityTitle: string
    currentUserId: string | null
    currentUserName: string | null
  } | null>(null)
  
  const { can } = usePermission()
  // Two genuinely different capabilities, split 2026-08-14 (§10.1) — they
  // coincide at admin-tier today but gate different actions: the resume
  // delete button below vs. the interview delete button inside
  // InterviewTab/InterviewListView (isAdmin prop, confirmed by tracing it
  // through both — a custom role ticked for one but not the other must
  // show/hide them independently.
  const canDeleteResume = can('resumes.delete')
  const canDeleteInterview = can('interviews.delete')
  // Added 2026-10-07 — same gap/fix as JobDetailPage.tsx's equivalent:
  // InterviewListView's Preview & Edit Questions, Retry, and Reanalyze
  // actions had no capability gate; Start Interview maps to
  // interviews.create instead. See that file's comment for the full
  // reasoning.
  const canManageInterviewContent = can('interviews.generate')
  const canCreateInterview = can('interviews.create')
  // Added 2026-08-25 (permission-catalog round 2, §26) — the download-
  // original/download-formatted actions had no gate of any kind until now;
  // default-open to all roles matching today's actual unrestricted
  // behavior exactly. Split 2026-08-26 into two independently grantable
  // keys, one per action below — both still default to all roles
  // (min_role stayed "viewer" for each), so nothing changes for anyone's
  // actual access.
  const canDownloadOriginal = can('resumes.downloadOriginal')
  const canDownloadFormatted = can('resumes.downloadFormatted')
  // Gates the Actions dropdown's trigger button itself, not just the items
  // inside it — bug fix 2026-08-30: the button rendered unconditionally
  // even for a role holding none of the five action capabilities below,
  // opening to a visually empty menu. OR of every item this dropdown can
  // contain (Download Original/Download Formatted/Reprocess/Blacklist/
  // Delete); hidden entirely when none apply, same hide convention as the
  // items themselves.
  const hasAnyResumeAction = canDownloadOriginal || canDownloadFormatted || can('resumes.reprocess') || can('resumes.blacklist') || canDeleteResume
  // Per-row "⋮" dropdown audit, 2026-08-30 — found ungated on this page's
  // matched-jobs list: "Reprocess comparison" reuses matching.newMatch
  // (same real-AI-compute concern as the standalone Apply Matching page's
  // "Trigger a new AI match run", not a new key); "View job profile"
  // reuses jobs.viewDetails, since it opens the exact same GET /jobs/{id}
  // endpoint that route already gates.
  const canNewMatch = can('matching.newMatch')
  const canViewJobDetails = can('jobs.viewDetails')

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        setShowResumeActionsDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [actionsDropdownRef])

  // Close job actions dropdown on scroll (only main page scroll, not modal scroll)
  useEffect(() => {
    const handleScroll = (event: Event) => {
      // Only close dropdown if scrolling the main page, not inside modals
      const target = event.target
      if (showJobActionsDropdown && (target === document || target === document.documentElement || target === document.body)) {
        setShowJobActionsDropdown(null)
      }
    }
    
    window.addEventListener('scroll', handleScroll, true) // Use capture phase to catch all scroll events
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [showJobActionsDropdown])
  
  // Helper function to get filename from resume data
  const getResumeFilename = (resumeData: any, response: any): string => {
    // Get filename from Content-Disposition header or use fallback
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'resume.pdf'; // Default filename
    
    if (contentDisposition) {
      // Try different patterns to extract filename
      const patterns = [
        /filename="([^"]+)"/,  // filename="file.pdf"
        /filename=([^;]+)/,    // filename=file.pdf
        /filename="?([^";\s]+)"?/  // filename=file.pdf or filename="file.pdf"
      ];
      
      for (const pattern of patterns) {
        const match = contentDisposition.match(pattern);
        if (match && match[1]) {
          filename = match[1].trim();
          break;
        }
      }
    }
    
    // Fallback: try to get filename from resume data
    if (filename === 'resume.pdf') {
      if (resumeData && resumeData.resume_filename) {
        // Use the resume_filename field (this is the primary field)
        filename = resumeData.resume_filename;
      } else if (resumeData && resumeData.filename) {
        // Use the original filename directly
        filename = resumeData.filename;
      } else if (resumeData && resumeData.candidate_name) {
        // Generate filename from candidate name
        const extension = response.headers['content-type']?.includes('pdf') ? 'pdf' : 'docx';
        filename = `${resumeData.candidate_name}_resume.${extension}`;
      }
    }
    
    return filename;
  }

  // New mutation to download the original resume file
  const downloadOriginalMutation = useMutation({
    mutationFn: ({ resumeId, jobId }: { resumeId: string; jobId?: string }) => resumesApi.download(resumeId, jobId),
    onSuccess: async (response) => {
      let extension = 'pdf'
      try {
        const ab = await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as ArrayBuffer)
          reader.onerror = reject
          reader.readAsArrayBuffer(response.data.slice(0, 4))
        })
        const bytes = new Uint8Array(ab)
        if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
          extension = 'pdf'
        } else if (bytes[0] === 0x50 && bytes[1] === 0x4B) {
          extension = 'docx'
        } else if (bytes[0] === 0xD0 && bytes[1] === 0xCF && bytes[2] === 0x11 && bytes[3] === 0xE0) {
          extension = 'doc'
        }
      } catch {
        extension = 'pdf'
      }

      const candidateName = resume?.candidate_name || ''
      const safeName = candidateName.replace(/[^a-zA-Z0-9\s]/g, '').trim() || 'Resume'
      const filename = `${safeName}_Resume.${extension}`
      const contentType = response.headers['content-type'] || 'application/octet-stream'
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Resume downloaded: ${filename}`);
    },
    onError: (error: any) => {
      toast.error('Failed to download original resume: ' + (error.response?.data?.detail || error.message));
    }
  })
  
  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    if (id) {
      deleteMutation.mutate()
    }
    setIsDeleteModalOpen(false)
  }

  // Determine the correct back path
  const fromJobPath = location.state?.from || null

  // Helper function to shorten Job ID
  const shortenJobId = (jobId: string): string => {
    if (!jobId) return ''
    return jobId.substring(0, 8)
  }

  // Fetch resume analysis from API
  const { data: resume, isLoading, error } = useQuery({
    queryKey: ['resume', id],
    queryFn: () => resumesApi.getById(id!).then(res => res.data),
    enabled: !!id,
    retry: 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Job search query
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs-search', searchQuery],
    queryFn: () => jobsApi.search({ query: searchQuery }),
    enabled: activeTab === 'matching' && !!searchQuery
  })

  // Fetch job matches for this candidate
  const { data: jobMatchesData, isLoading: jobMatchesLoading } = useQuery({
    queryKey: ['job-matches', id],
    queryFn: () => matchingApi.getJobMatchesForCandidate(id!),
    enabled: activeTab === 'matching' && !!id,
    staleTime: 0,
  })

  // Poll every 5s while any job match is pending/processing
  useEffect(() => {
    if (activeTab !== 'matching' || !id) return
    const data = jobMatchesData?.data
    if (!data) return
    const isActive = (data.job_matches || []).some(
      (r: any) => r.status === 'pending' || r.status === 'processing'
    )
    if (!isActive) return
    const timer = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['job-matches', id] })
    }, 5000)
    return () => clearTimeout(timer)
  }, [jobMatchesData, activeTab, id, queryClient])

  // Load job matches when switching to matching tab
  useEffect(() => {
    if (activeTab === 'matching' && id && jobMatchesData) {
      if (jobMatchesData.data.job_matches && jobMatchesData.data.job_matches.length > 0) {
        setComparisonResults(jobMatchesData.data)
        setShowComparison(true)
        
        // Load candidate statuses (candidate_status_id) for all job matches
        const statuses: Record<string, string> = {}
        for (const result of jobMatchesData.data.job_matches) {
          // Use the candidate_status_id from the result if set, otherwise leave unset
          statuses[result.job_id] = result.candidate_status_id || ''
        }
        setApplicationStatuses(statuses)
      }
    }
  }, [activeTab, id, jobMatchesData])

  const jobs = jobsData?.data?.jobs || []

  // Get list of job IDs already in analysis results
  const getAlreadyAnalyzedJobs = () => {
    if (!comparisonResults?.job_matches) return []
    return comparisonResults.job_matches.map((result: any) => result.job_id)
  }

  const alreadyAnalyzedJobs = getAlreadyAnalyzedJobs()

  // Job matching handler functions
  const handleJobSelect = (jobId: string) => {
    // Don't allow selection if job is already analyzed
    if (alreadyAnalyzedJobs.includes(jobId)) {
      return
    }
    
    // Toggle selection - add if not selected, remove if selected
    if (selectedJobs.includes(jobId)) {
      setSelectedJobs(prev => prev.filter(id => id !== jobId))
    } else {
      setSelectedJobs(prev => [...prev, jobId])
    }
  }

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setSearchQuery(searchTerm.trim())
    } else {
      setSearchQuery('')
    }
  }

  const handleClearSearch = () => {
    setSearchTerm('')
    setSearchQuery('')
    setSelectedJobs([])
  }

  // Candidate status handler (newStatus is a candidate_status_id; comment is required)
  const handleApplicationStatusChange = async (jobId: string, newStatus: string, comment: string) => {
    if (!id) return

    try {
      // Optimistic update
      setApplicationStatuses(prev => ({ ...prev, [jobId]: newStatus }))

      // Call API to update status (note: for resume page, we use jobId as first param, resumeId as second)
      const response = await matchingApi.updateApplicationStatus(jobId, id, newStatus, comment)
      const updated = response.data || {}

      // Update comparisonResults state to persist the change (stage/status
      // names and last-updated feed the applications table and summary)
      setComparisonResults((prev: any) => {
        if (!prev?.job_matches) return prev
        return {
          ...prev,
          job_matches: prev.job_matches.map((match: any) =>
            match.job_id === jobId
              ? {
                  ...match,
                  candidate_status_id: newStatus,
                  status_name: updated.status_name || match.status_name,
                  stage_name: updated.stage_name || match.stage_name,
                  status_updated_at: updated.updated_at || match.status_updated_at,
                }
              : match
          )
        }
      })

      // Refetch so status_updated_by_name comes from the server
      queryClient.invalidateQueries({ queryKey: ['job-matches', id] })

      // Invalidate audit history to show the new status change
      queryClient.invalidateQueries({ queryKey: ['audit-history'] })

      console.log(`Successfully updated candidate status for job ${jobId} to ${newStatus}`)
    } catch (error) {
      console.error('Failed to update candidate status:', error)
      // Revert optimistic update on error
      setApplicationStatuses(prev => {
        const oldStatus = comparisonResults?.job_matches?.find(
          (r: any) => r.job_id === jobId
        )?.candidate_status_id || ''
        return { ...prev, [jobId]: oldStatus }
      })
      toast.error('Failed to update status. Please try again.')
    }
  }

  // Handler for reprocessing job match
  const handleReprocessJobMatch = async (jobId: string) => {
    if (!id) return

    try {
      setReprocessingJobs(prev => new Set([...prev, jobId]))

      // Call the reprocess API endpoint
      await matchingApi.reprocessComparisonRecord(jobId, id)

      // Invalidate the query to refetch updated results
      await queryClient.invalidateQueries({ queryKey: ['job-matches', id] })

      toast.success('Reprocessing started for job match')
    } catch (error) {
      console.error('Failed to reprocess job match:', error)
      toast.error('Failed to reprocess job match. Please try again.')
    } finally {
      setTimeout(() => {
        setReprocessingJobs(prev => {
          const newSet = new Set(prev)
          newSet.delete(jobId)
          return newSet
        })
      }, 2000)
    }
  }

  // "Retry" on a row whose Analysis Failed -- unlike handleReprocessJobMatch
  // above (matching-only, silently no-ops if this résumé's own analysis
  // never completed), this re-runs analysis on THIS SAME résumé record
  // (this page's own `id`) via resumes.reprocess(job_id=...) and lets the
  // backend re-trigger matching against that job once it succeeds.
  // Deliberately not a re-upload: that goes through duplicate detection and
  // creates a second résumé record for the same person.
  const handleRetryJobMatch = async (jobId: string) => {
    if (!id) return

    try {
      setReprocessingJobs(prev => new Set([...prev, jobId]))
      await resumesApi.reprocess(id, jobId)
      await queryClient.invalidateQueries({ queryKey: ['job-matches', id] })
      toast.success('Retrying analysis for this job match')
    } catch (error) {
      console.error('Failed to retry job match:', error)
      toast.error('Failed to retry. Please try again.')
    } finally {
      setTimeout(() => {
        setReprocessingJobs(prev => {
          const newSet = new Set(prev)
          newSet.delete(jobId)
          return newSet
        })
      }, 2000)
    }
  }

  // Handler for generating interview questions
  const handleGenerateInterview = async (jobId: string, level: 'L1' | 'L2' = 'L2'): Promise<boolean> => {
    if (!id) return false

    try {
      setSelectedJobForInterview(jobId)
      
      // Set specific loading state
      if (level === 'L1') {
        setL1Loading(jobId)
      } else {
        setL2Loading(jobId)
      }

      // Open modal immediately with loading state
      setInterviewLoading(true)
      setShowInterviewModal(true)

      if (level === 'L1') {
        // L1: Use the job's existing interview questions
        // Fetch job details first
        const jobResponse = await jobsApi.getById(jobId)
        const job = jobResponse.data
        
        if (job.interview_questions && typeof job.interview_questions === 'string') {
          setInterviewGuide({ questions: job.interview_questions, isJobLevel: true })
          setInterviewLoading(false) // Data is ready
          return true
        } else {
          toast.error('No general interview questions found for this job.')
          setShowInterviewModal(false) // Close modal on error
          return false
        }
      } else {
        // L2: Generate customized questions based on resume + JD
        const response = await interviewApi.generateInterview({
          resume_id: id,
          job_id: jobId,
          interview_level: 'L2'
        })
        
        // Update modal content after successful API response
        if (response && response.guide) {
          setInterviewGuide(response.guide)
          setInterviewLoading(false) // Data is ready
          
          if (response.is_new_generation) {
            toast.success('Custom interview questions generated successfully!')
          }
          
          // Ensure minimum loading time for better UX
          await new Promise(resolve => setTimeout(resolve, 1000))
          return true
        } else {
          toast.error('Failed to generate interview questions. Please try again.')
          setShowInterviewModal(false) // Close modal on error
          return false
        }
      }
    } catch (error) {
      console.error('Failed to process interview questions:', error)
      toast.error('Failed to process interview questions. Please try again.')
      setShowInterviewModal(false) // Close modal on error
      return false
    } finally {
      // Clear specific loading state immediately since we now have minimum loading time in the try block
      if (level === 'L1') {
        setL1Loading(null)
      } else {
        setL2Loading(null)
      }
    }
  }

  // Handler for reprocessing interview
  const handleReprocessInterview = async () => {
    if (!id || !selectedJobForInterview) return
    
    try {
      setInterviewReprocessing(true)
      
      const response = await interviewApi.reprocessInterview({
        resume_id: id,
        job_id: selectedJobForInterview,
        interview_level: 'L2'
      })
      
      setInterviewGuide(response.guide)
      toast.success('Interview questions regenerated successfully!')
    } catch (error) {
      console.error('Failed to reprocess interview questions:', error)
      toast.error('Failed to regenerate interview questions. Please try again.')
    } finally {
      setInterviewReprocessing(false)
    }
  }

  // Handler for closing interview modal
  const handleCloseInterviewModal = () => {
    setShowInterviewModal(false)
    setInterviewGuide(null)
    setSelectedJobForInterview(null)
    setInterviewLoading(false)
  }

  // Handler for viewing job profile
  const handleViewJobProfile = (jobId: string) => {
    setSelectedJobId(jobId)
    setShowJobPopup(true)
  }

  // Assignment tracking handlers
  const handleEditAssignment = (
    entityType: 'resume_upload' | 'job_match',
    entityId: string,
    entityTitle: string,
    currentUserId: string | null,
    currentUserName: string | null
  ) => {
    setSelectedAssignment({
      entityType,
      entityId,
      entityTitle,
      currentUserId,
      currentUserName,
    })
    setShowAssignmentEdit(true)
  }

  const handleViewAuditTrail = (
    entityType: 'resume_upload' | 'job_match',
    entityId: string,
    entityTitle: string
  ) => {
    setSelectedAssignment({
      entityType,
      entityId,
      entityTitle,
      currentUserId: null,
      currentUserName: null,
    })
    setShowAuditTrail(true)
  }

  const handleAssignmentSaved = () => {
    // Refresh job matches data
    queryClient.invalidateQueries({ queryKey: ['job-matches', id] })
    toast.success('Assignment updated successfully')
  }

  // Check if user can edit assignments — this page's own assignment
  // controls are all for the job_match entities in its comparison-results
  // list ('job_match' is the only entityType ever actually passed to
  // handleEditAssignment/handleViewAuditTrail below), not this resume's
  // own resume_upload assignment, so this gates on the job-match split key.
  const canEditAssignments = can('assignments.manageJobMatches')
  // Can change a candidate's status (Apply Matching tab)
  const canChangeCandidateStatus = can('candidateStatus.change')

  const handleCompareJobs = async () => {
    if (!id || selectedJobs.length === 0) {
      toast.error('Please select at least one job to compare')
      return
    }

    try {
      setComparisonLoading(true)
      
      // Use the compare-candidates-async API to start comparison
      const response = await matchingApi.compareCandidatesAsync(selectedJobs, [id])
      
      if (response.data) {
        setComparisonResults(response.data)
        setShowComparison(true)
        
        // Refresh the job matches data to show updated results
        await queryClient.invalidateQueries({ queryKey: ['job-matches', id] })
        
        // Clear search results and selections
        setSearchTerm('')
        setSearchQuery('')
        setSelectedJobs([])
      } else {
        toast.error('Failed to start job comparison')
      }
    } catch (error) {
      console.error('Error starting job comparison:', error)
      toast.error('Failed to start job comparison')
    } finally {
      setComparisonLoading(false)
    }
  }

  // Reprocess mutation
  const reprocessMutation = useMutation({
    mutationFn: () => resumesApi.reprocess(id!),
    onSuccess: () => {
      toast.success('Resume reprocessing started successfully')
      queryClient.invalidateQueries({ queryKey: ['resume', id] })
    },
    onError: (error: any) => {
      toast.error('Failed to reprocess resume: ' + (error.response?.data?.detail || error.message))
    }
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => resumesApi.delete(id!),
    onSuccess: () => {
      toast.success('Resume deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      navigate('/resumes')
    },
    onError: (error: any) => {
      toast.error('Failed to delete resume: ' + (error.response?.data?.detail || error.message))
    }
  })

  // Blacklist mutation
  const blacklistMutation = useMutation({
    mutationFn: () => resumesApi.blacklistDuplicates({ blacklisted_resume_ids: [id!] }),
    onSuccess: () => {
      toast.success('Resume blacklisted successfully')
      queryClient.invalidateQueries({ queryKey: ['resume', id] })
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
    },
    onError: (error: any) => {
      toast.error('Failed to blacklist resume: ' + (error.response?.data?.detail || error.message))
    }
  })

  // Unblacklist mutation
  const unblacklistMutation = useMutation({
    mutationFn: () => resumesApi.unblacklistDuplicates({ unblacklisted_resume_ids: [id!] }),
    onSuccess: () => {
      toast.success('Resume unblacklisted successfully')
      queryClient.invalidateQueries({ queryKey: ['resume', id] })
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
    },
    onError: (error: any) => {
      toast.error('Failed to unblacklist resume: ' + (error.response?.data?.detail || error.message))
    }
  })

  // Download clean resume mutation
  const downloadMutation = useMutation({
    mutationFn: () => resumeGenerationApi.generateCleanResume(id!),
    onSuccess: (response) => {
      // Dismiss the loading toast
      toast.dismiss(`resume-download-${id}`)

      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Generate filename
      const candidateName = resume?.candidate_name || 'Professional'
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
      link.download = `${candidateName}_Resume_${timestamp}.docx`
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Formatted resume downloaded successfully!')
    },
    onError: (error: any) => {
      // Dismiss the loading toast
      toast.dismiss(`resume-download-${id}`)
      toast.error('Failed to generate resume: ' + (error.response?.data?.detail || error.message))
    }
  })

  // Download clean resume function
  const downloadCleanResume = () => {
    if (!resume?.analysis) {
      toast.error('Resume analysis not available. Please ensure the resume has been processed.')
      return
    }
    toast.loading(`Preparing formatted resume...`, { 
      id: `resume-download-${id}` 
    })
    downloadMutation.mutate()
  }

  // Download resume as Word document function
  /* commenting out the download as word button for now
  const handleDownloadResume = async () => {
    if (!resume) {
      toast.error('Resume data not available')
      return
    }
    
    try {
      await downloadResumeAsWord(resume)
      toast.success('Resume downloaded successfully')
    } catch (error: any) {
      console.error('Download error:', error)
      const message = error.response?.data?.detail || 'Download failed'
      toast.error(message)
    }
  }
  */

  // Helper functions

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600'
    if (score >= 0.6) return 'text-yellow-600'
    return 'text-red-600'
  }

  // Status badge component
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const statusConfig = {
      processing: { icon: ClockIcon, text: 'Processing', className: 'bg-yellow-100 text-yellow-800' },
      analyzed: { icon: CheckCircleIcon, text: 'Analyzed', className: 'bg-green-100 text-green-800' },
      failed: { icon: ExclamationTriangleIcon, text: 'Failed', className: 'bg-red-100 text-red-800' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.analyzed
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        <Icon className="w-4 h-4 mr-2" />
        {config.text}
      </span>
    )
  }

  // Tab navigation - filter tabs based on user role.
  // Résumé Profile redesign (2026-10-01): a TalentOS-analysed résumé
  // (resume.analysis_engine === 'talentos') shows ONE "Resume Profile" tab
  // instead of Overview/Skills Analysis/Experience/Education -- reuses the
  // 'overview' tab id so the existing URL-param/localStorage default-tab
  // logic above needs no changes; only this tab's label and its content
  // block (search `activeTab === 'overview'` below) differ per engine.
  // Analysis and Job Matching stay identical either way. Gated on
  // SHOW_TALENTOS_RESUME_UI (false for now) so nothing changes yet.
  const isTalentosProfile = SHOW_TALENTOS_RESUME_UI && resume?.analysis_engine === 'talentos'
  const allTabs = isTalentosProfile
    ? [
        { id: 'overview', name: 'Resume Profile', icon: EyeIcon },
        { id: 'agents', name: 'Analysis', icon: BeakerIcon },
        { id: 'matching', name: 'Job Matching', icon: UserGroupIcon },
        // { id: 'interview', name: 'Interview', icon: MicrophoneIcon }, // hidden per request — tab + content commented out below
      ]
    : [
        { id: 'overview', name: 'Overview', icon: EyeIcon },
        { id: 'skills', name: 'Skills Analysis', icon: CpuChipIcon },
        { id: 'experience', name: 'Experience', icon: BriefcaseIcon },
        { id: 'education', name: 'Education', icon: AcademicCapIcon },
        { id: 'agents', name: 'Analysis', icon: BeakerIcon },
        { id: 'matching', name: 'Job Matching', icon: UserGroupIcon },
        // { id: 'interview', name: 'Interview', icon: MicrophoneIcon }, // hidden per request — tab + content commented out below
      ]

  // Filter tabs based on user role and blacklist status
  const tabs = allTabs.filter(tab => {
    if (tab.id === 'matching') {
      return !resume?.blacklist // Hide Apply Matching tab for blacklisted candidates
    }
    if (tab.id === 'interview') {
      // Resolved 2026-08-12 (AI_RECRUIT_DYNAMIC_RBAC_MASTER_PLAN.md §7) — this
      // used to be `isAdmin || user?.role === 'senior_recruiter'`, excluding
      // `manager`, while JobDetailPage's equivalent check included manager.
      // Checked git history for the original reasoning: none exists — the
      // pre-migration code was `isAdmin || isSeniorRecruiter // Only show
      // Interview tab to admins and senior recruiters`, no comment on why
      // manager was left out. Resolving toward JobDetailPage's version:
      // manager already holds interviews.forceStatus (can override interview
      // status outright), so excluding them from merely *viewing* this tab
      // didn't hold together as a deliberate rule.
      //
      // Converted 2026-08-14 (§10.1) to an OR across the interview-tier
      // capabilities rather than a single proxy: this is whole-tab-container
      // visibility, never tied to one specific action (confirmed via the
      // history above — it was already a coarse admin-or-senior_recruiter
      // heuristic before this project). A single proxy would only be
      // correct by coincidence, since all three happen to share a tier
      // today; a custom role ticked for one but not the others would make
      // a single check silently wrong in a way this OR doesn't.
      return can('interviews.create') || can('interviews.generate') || can('interviewGuides.generate')
    }
    return true // Show all other tabs to everyone
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (error || !resume) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-400 mx-auto" />
          <p className="mt-4 text-gray-600">Failed to load resume analysis</p>
          <button
            onClick={() => navigate('/resumes')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Candidate
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
              if (fromJobPath) {
                  // The path already has the search query, we just add the tab hash
                  navigate(`${fromJobPath}#matching`);
              } else {
                  // Use browser back to preserve search params
                  navigate(-1);
              }
          }}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1.5" />
          Back to Candidate
        </button>
      </div>

      {/* Main Candidate Information */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                <span>{resume.candidate_name || resume.resume_filename}</span>
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-mono">
                  {resume.candidate_id}
                </span>
              </h1>
              {(resume.analysis?.personal_info?.professional_title || resume.professional_title) && (
                <div className="mt-1">
                  <span className="text-sm text-blue-600 font-medium">
                    {resume.analysis?.personal_info?.professional_title || resume.professional_title}
                  </span>
                </div>
              )}
              <div className="flex items-center space-x-3 mt-1">
                <StatusBadge status={resume.status} />
                {resume.blacklist && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200">
                    Blacklisted
                  </span>
                )}
                {resume.source_name && (
                  <div className="flex items-center text-xs text-gray-500">
                    <TagIcon className="w-3.5 h-3.5 mr-1" />
                    {resume.source_name}
                    {resume.source_is_active === false && (
                      <span className="text-xs text-gray-400 ml-1">(Inactive)</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Action Dropdown - trigger button hidden entirely when no action
                inside is available to this role, bug fix 2026-08-30 (see
                hasAnyResumeAction above). */}
            {hasAnyResumeAction && (
            <div className="relative" ref={actionsDropdownRef}>
                <button
                    onClick={() => setShowResumeActionsDropdown(!showResumeActionsDropdown)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                    <span>Actions</span>
                    <ChevronDownIcon className="-mr-1 ml-2 h-4 w-4" />
                </button>
                {showResumeActionsDropdown && (
                <div className="origin-top-right absolute right-0 mt-2 w-52 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                    <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        {/* Download Original Resume */}
                        {canDownloadOriginal && (
                            <button
                                onClick={() => {
                                    if (id) downloadOriginalMutation.mutate({ resumeId: id });
                                    setShowResumeActionsDropdown(false);
                                }}
                                disabled={downloadOriginalMutation.isPending}
                                className="w-full text-left flex items-center px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                role="menuitem"
                            >
                                <ArrowDownTrayIcon className="mr-2 h-4 w-4 text-gray-400" />
                                <span>Download Original</span>
                            </button>
                        )}

                        {/* Download Formatted Resume */}
                        {canDownloadFormatted && (
                            <button
                                onClick={() => {
                                    downloadCleanResume();
                                    setShowResumeActionsDropdown(false);
                                }}
                                disabled={downloadMutation.isPending || !resume.analysis}
                                className="w-full text-left flex items-center px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                role="menuitem"
                            >
                                <DocumentTextIcon className="mr-2 h-4 w-4 text-gray-400" />
                                <span>Download Formatted</span>
                            </button>
                        )}

                        {/* Below-the-divider actions require at least senior_recruiter */}
                        {can('resumes.reprocess') && <div className="border-t border-gray-100 my-1" />}

                        {/* Reprocess Action — senior_recruiter+ (RBAC_UI_IMPLEMENTATION_PLAN.md
                            §5.6.E), disabled (not hidden) below that tier per §5.7 */}
                        {can('resumes.reprocess') && (
                            <button
                                onClick={() => {
                                    reprocessMutation.mutate();
                                    setShowResumeActionsDropdown(false);
                                }}
                                disabled={reprocessMutation.isPending}
                                className="w-full text-left flex items-center px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                role="menuitem"
                            >
                                <ArrowPathIcon className="mr-2 h-4 w-4 text-gray-400" />
                                <span>Reprocess</span>
                            </button>
                        )}

                        {/* Blacklist/Unblacklist Action — hidden (not disabled) without
                            resumes.blacklist, bug fix 2026-08-27 (was deliberately
                            disabled-only per the now-stale RBAC_UI_IMPLEMENTATION_PLAN.md
                            §5.6.L — superseded by the app-wide hide convention). */}
                        {can('resumes.blacklist') && (resume.blacklist ? (
                            <button
                                onClick={() => {
                                    unblacklistMutation.mutate();
                                    setShowResumeActionsDropdown(false);
                                }}
                                disabled={unblacklistMutation.isPending}
                                className="w-full text-left flex items-center px-3 py-1.5 text-xs text-green-700 hover:bg-green-50 disabled:opacity-50"
                                role="menuitem"
                            >
                                <CheckIcon className="mr-2 h-4 w-4 text-green-400" />
                                <span>Unblacklist Resume</span>
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    blacklistMutation.mutate();
                                    setShowResumeActionsDropdown(false);
                                }}
                                disabled={blacklistMutation.isPending}
                                className="w-full text-left flex items-center px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                                role="menuitem"
                            >
                                <NoSymbolIcon className="mr-2 h-4 w-4 text-red-400" />
                                <span>Blacklist Resume</span>
                            </button>
                        ))}

                        {/* Delete Action - Admin Only */}
                        {canDeleteResume && (
                            <button
                                onClick={() => {
                                    handleOpenDeleteModal();
                                    setShowResumeActionsDropdown(false);
                                }}
                                disabled={deleteMutation.isPending}
                                className="w-full text-left flex items-center px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                                role="menuitem"
                            >
                                <TrashIcon className="mr-2 h-4 w-4 text-red-400" />
                                <span>Delete</span>
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
        )}
          </div>
        </div>
      </div>

      {/* Duplicate Detection Warning */}
      {resume.is_duplicate && (
        <div className={`rounded-lg border-l-4 p-4 ${
          (resume.duplicate_confidence || 0) >= 0.8 
            ? 'bg-red-50 border-red-400' 
            : (resume.duplicate_confidence || 0) >= 0.6 
            ? 'bg-yellow-50 border-yellow-400'
            : 'bg-orange-50 border-orange-400'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              <DocumentDuplicateIcon className={`h-5 w-5 ${
                (resume.duplicate_confidence || 0) >= 0.8 
                  ? 'text-red-400' 
                  : (resume.duplicate_confidence || 0) >= 0.6 
                  ? 'text-yellow-400'
                  : 'text-orange-400'
              }`} />
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                (resume.duplicate_confidence || 0) >= 0.8 
                  ? 'text-red-800' 
                  : (resume.duplicate_confidence || 0) >= 0.6 
                  ? 'text-yellow-800'
                  : 'text-orange-800'
              }`}>
                Potential Duplicate Detected ({Math.round((resume.duplicate_confidence || 0) * 100)}% confidence)
              </h3>
              <div className={`mt-2 text-sm ${
                (resume.duplicate_confidence || 0) >= 0.8 
                  ? 'text-red-700' 
                  : (resume.duplicate_confidence || 0) >= 0.6 
                  ? 'text-yellow-700'
                  : 'text-orange-700'
              }`}>
                <p>{resume.duplicate_reasoning}</p>
                {resume.duplicate_matches && resume.duplicate_matches.length > 0 && (
                  <div className="mt-3">
                    <p className="font-medium">Similar resumes found:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      {resume.duplicate_matches.slice(0, 3).map((match: any, index: number) => (
                        <li key={index}>
                          <span className="font-medium">{match.candidate_name || match.filename}</span>
                          <span className="text-xs ml-2">
                            ({match.match_type} match, {Math.round(match.confidence * 100)}% confidence)
                          </span>
                        </li>
                      ))}
                      {resume.duplicate_matches.length > 3 && (
                        <li className="text-xs">...and {resume.duplicate_matches.length - 3} more</li>
                      )}
                    </ul>
                  </div>
                )}
                {(resume.duplicate_confidence || 0) >= 0.8 && (
                  <div className="mt-3 flex items-center space-x-2">
                    <ShieldExclamationIcon className="h-4 w-4 text-red-500" />
                    <span className="text-xs font-medium">High confidence duplicate - manual review recommended</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto scrollbar-hide px-4">
            <div className="flex space-x-6 min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    const newTab = tab.id
                    setActiveTab(newTab)
                    if (id) {
                      try { localStorage.setItem(`resume:tab:${id}`, newTab) } catch {}
                    }
                    const newParams = new URLSearchParams(searchParams)
                    newParams.set('tab', newTab)
                    setSearchParams(newParams, { replace: true })
                  }}
                    className={`py-2.5 px-1 border-b-2 font-medium text-xs flex items-center space-x-1.5 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="hidden sm:inline">{tab.name}</span>
                    <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
                </button>
              )
            })}
            </div>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-3">

          {activeTab === 'overview' && isTalentosProfile && (
            <TalentosResumeProfileTab resumeId={id!} />
          )}

          {activeTab === 'overview' && !isTalentosProfile && (
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              {/* Professional Summary */}
              <CollapsibleCard title="Professional Summary" icon={<DocumentTextIcon className="w-4 h-4 text-blue-600" />} defaultOpen compact>
                <div className="bg-white border border-blue-100 rounded-lg p-3">
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">
                    {resume.analysis?.final_summary || resume.analysis?.personal_info?.professional_title || 'Summary not available.'}
                  </p>
                </div>
              </CollapsibleCard>

              {/* Personal Information */}
              <CollapsibleCard title="Personal Information" icon={<UserIcon className="w-4 h-4 text-blue-600" />} defaultOpen compact>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <UserIcon className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm text-gray-900">{resume.candidate_name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <EnvelopeIcon className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm text-gray-900">{resume.analysis?.personal_info?.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <PhoneIcon className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm text-gray-900">{formatPhoneNumber(resume.analysis?.personal_info?.phone || resume.candidate_phone)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPinIcon className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm text-gray-900">{resume.analysis?.personal_info?.location}</span>
                  </div>
                </div>
              </CollapsibleCard>

              {/* Key Strengths */}
              {resume.analysis?.quality_assessment?.strengths && (
                <CollapsibleCard title="Key Strengths" defaultOpen compact>
                  <ul className="space-y-1.5">
                    {resume.analysis.quality_assessment.strengths.map((strength: string, index: number) => (
                      <li key={index} className="flex items-start space-x-2">
                        <CheckCircleIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-blue-800">{strength}</span>
                      </li>
                    ))}
                  </ul>
                </CollapsibleCard>
              )}

              {/* Processing Info */}
              {/* Commenting out processing info for cleaner UI */}
              {/*
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Processing Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Resume ID:</span>
                    <div className="font-medium font-mono text-xs">{resume.id}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <div className="font-medium capitalize">{resume.status}</div>
                  </div>
                  {resume.confidence_score && (
                    <div>
                      <span className="text-gray-600">AI Confidence:</span>
                      <div className="font-medium">{Math.round(resume.confidence_score * 100)}%</div>
                    </div>
                  )}
                  {resume.processing_time && (
                    <div>
                      <span className="text-gray-600">Processing Time:</span>
                      <div className="font-medium">{Math.round(resume.processing_time)}s</div>
                    </div>
                  )}
                  {resume.error_message && (
                    <div className="col-span-full">
                      <span className="text-red-600">Error:</span>
                      <div className="font-medium text-red-700 mt-1">{resume.error_message}</div>
                    </div>
                  )}
                </div>
              </div>
              */}
            </div>
          )}

          {activeTab === 'skills' && (
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              {/* Enhanced Skills Analysis - COMMENTED OUT */}
              {/* Skills Analysis Summary  and  Skills Statistics */} 
              {/*
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center space-x-2">
                  <CpuChipIcon className="w-6 h-6 text-purple-600" />
                  <span>Enhanced Skills Analysis</span>
                </h3>
                <p className="text-gray-600 mb-4">
                  AI-powered comprehensive skills extraction with proficiency levels, relevance scoring, and detailed categorization.
                </p>
    
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {resume.analysis?.skills_analysis?.technical_skills?.length || 0}
                    </div>
                    <div className="text-sm text-purple-700">Technical Skills</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {resume.analysis?.skills_analysis?.soft_skills?.length || 0}
                    </div>
                    <div className="text-sm text-blue-700">Soft Skills</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {Object.keys(resume.analysis?.skills_analysis?.skill_categories || {}).length}
                    </div>
                    <div className="text-sm text-green-700">Categories</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {Object.keys(resume.analysis?.skills_analysis?.skill_proficiency || {}).length}
                    </div>
                    <div className="text-sm text-orange-700">Proficiency Levels</div>
                  </div>
                </div>
              </div>
              */}

              {/* Core Competencies (aggregated from detailed_table) */}
              {resume.analysis?.skills_analysis?.detailed_table && (
                (() => {
                  const dt = resume.analysis!.skills_analysis!.detailed_table as Record<string, any[]>;
                  const allRows: any[] = Object.values(dt).flat().filter(Array.isArray).length
                    ? (Object.values(dt).flat() as any[])
                    : ([] as any[]);
                  const isCore = (row: any) =>
                    String(row?.core_or_supporting || '').toLowerCase().startsWith('core');
                  const coreRows = allRows
                    .filter((r) => isCore(r))
                    .sort((a, b) => (typeof b.relevance_score === 'number' ? b.relevance_score : 0) - (typeof a.relevance_score === 'number' ? a.relevance_score : 0));
                  const seen = new Set<string>();
                  const topCore = coreRows.filter((r) => {
                    const key = String(r.skill || '').toLowerCase();
                    if (!key || seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  }).slice(0, 12);
                  if (!topCore.length) return null;
                  return (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                        <StarIcon className="w-5 h-5 text-yellow-500" />
                        <span>Core Competencies</span>
                        <span className="text-sm text-gray-500 font-normal">(Ranked by AI Relevance Score)</span>
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {topCore.map((r, i) => (
                          <span key={`${r.skill}-${i}`} className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 text-blue-800 px-3 py-2 rounded-full text-sm font-medium border border-blue-200">
                            {r.skill}
                            {typeof r.relevance_score === 'number' && (
                              <span className="ml-2 text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                                {Math.round(r.relevance_score * 100)}%
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()
              )}
              {/* Technical Skills */}
              {resume.analysis?.skills_analysis?.technical_skills && (
                <CollapsibleCard title="Technical Skills" icon={<CpuChipIcon className="w-4 h-4 text-blue-600" />} defaultOpen compact>
                  <div className="flex flex-wrap gap-1.5">
                    {resume.analysis.skills_analysis.technical_skills.map((skill: any, index: number) => (
                      <span key={index} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {typeof skill === 'string' ? skill : skill?.skill || skill?.name || String(skill)}
                      </span>
                    ))}
                  </div>
                </CollapsibleCard>
              )}

              {/* Programming Languages */}
              {resume.analysis?.skills_analysis?.programming_languages && (
                <CollapsibleCard title="Programming Languages" defaultOpen compact>
                  <div className="flex flex-wrap gap-1.5">
                    {resume.analysis.skills_analysis.programming_languages.map((lang: any, index: number) => (
                      <span key={index} className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {typeof lang === 'string' ? lang : lang?.skill || lang?.name || String(lang)}
                      </span>
                    ))}
                  </div>
                </CollapsibleCard>
              )}

              {/* Frameworks & Libraries */}
              {resume.analysis?.skills_analysis?.frameworks_libraries && (
                <CollapsibleCard title="Frameworks & Libraries" defaultOpen compact>
                  <div className="flex flex-wrap gap-1.5">
                    {resume.analysis.skills_analysis.frameworks_libraries.map((framework: any, index: number) => (
                      <span key={index} className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {typeof framework === 'string' ? framework : framework?.skill || framework?.name || String(framework)}
                      </span>
                    ))}
                  </div>
                </CollapsibleCard>
              )}

              {/* Tools & Technologies */}
              {resume.analysis?.skills_analysis?.tools_technologies && (
                <CollapsibleCard title="Tools & Technologies" defaultOpen compact>
                  <div className="flex flex-wrap gap-1.5">
                    {resume.analysis.skills_analysis.tools_technologies.map((tool: any, index: number) => (
                      <span key={index} className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {typeof tool === 'string' ? tool : tool?.skill || tool?.name || String(tool)}
                      </span>
                    ))}
                  </div>
                </CollapsibleCard>
              )}

              {/* Soft Skills */}
              {resume.analysis?.skills_analysis?.soft_skills && (
                <CollapsibleCard title="Soft Skills & Leadership" defaultOpen compact>
                  <div className="flex flex-wrap gap-1.5">
                    {resume.analysis.skills_analysis.soft_skills.map((skill: any, index: number) => (
                      <span key={index} className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {typeof skill === 'string' ? skill : skill?.skill || skill?.name || String(skill)}
                      </span>
                    ))}
                  </div>
                </CollapsibleCard>
              )}

              {/* Skill Categories */}
              {resume.analysis?.skills_analysis?.skill_categories && (
                <CollapsibleCard title="Skill Categories" defaultOpen compact>
                  <div className="space-y-3">
                    {Object.entries(resume.analysis.skills_analysis.skill_categories).map(([category, skills]: [string, any], index: number) => (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-gray-900 mb-1.5 capitalize">{category}</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {Array.isArray(skills) && skills.map((skill: any, skillIndex: number) => (
                            <span key={skillIndex} className="bg-white text-gray-700 px-2 py-0.5 rounded text-xs border">
                              {typeof skill === 'string' ? skill : skill?.skill || skill?.name || String(skill)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleCard>
              )}

              {/* Certifications */}
              {resume.analysis?.skills_analysis?.certifications && (
                <CollapsibleCard title="Certifications" defaultOpen compact>
                  <div className="flex flex-wrap gap-1.5">
                    {resume.analysis.skills_analysis.certifications.map((cert: any, index: number) => (
                      <span key={index} className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {typeof cert === 'string' ? cert : cert?.skill || cert?.name || String(cert)}
                      </span>
                    ))}
                  </div>
                </CollapsibleCard>
              )}

              {/* Comprehensive Skills Table */}
              {(() => {
                // Aggregate all skills from different categories into one table
                const skillsAnalysis = resume.analysis?.skills_analysis;
                if (!skillsAnalysis) return null;

                const allSkills: any[] = [];
                
                // Helper function to add skills from a category
                const addSkillsFromCategory = (skills: any[], category: string) => {
                  if (Array.isArray(skills)) {
                    skills.forEach(skill => {
                      if (typeof skill === 'object' && skill.skill) {
                        allSkills.push({ ...skill, category });
                      } else if (typeof skill === 'string') {
                        allSkills.push({ 
                          skill, 
                          category,
                          proficiency: '-',
                          relevance_score: 0.5,
                          frequency_of_use: '-',
                          years_experience: '-',
                          core_or_supporting: 'Supporting',
                          current_or_outdated: 'Current',
                          context_demonstrated: '-',
                          evidence_of_expertise: '-',
                          emerging: false
                        });
                      }
                    });
                  }
                };

                // Add skills from all categories
                addSkillsFromCategory(skillsAnalysis.technical_skills, 'Technical');
                addSkillsFromCategory(skillsAnalysis.programming_languages, 'Programming');
                addSkillsFromCategory(skillsAnalysis.frameworks_libraries, 'Frameworks');
                addSkillsFromCategory(skillsAnalysis.tools_technologies, 'Tools');
                addSkillsFromCategory(skillsAnalysis.soft_skills, 'Soft Skills');
                addSkillsFromCategory(skillsAnalysis.languages, 'Languages');
                addSkillsFromCategory(skillsAnalysis.methodologies, 'Methodologies');
                addSkillsFromCategory(skillsAnalysis.certifications, 'Certifications');
                addSkillsFromCategory(skillsAnalysis.emerging_skills, 'Emerging');
                addSkillsFromCategory(skillsAnalysis.key_competencies, 'Key Competencies');

                if (allSkills.length === 0) return null;

                // Remove duplicates based on skill name
                const uniqueSkills = allSkills.filter((skill, index, self) => 
                  index === self.findIndex(s => s.skill.toLowerCase() === skill.skill.toLowerCase())
                );

                // Sort by relevance score and core skills first
                const sortedSkills = uniqueSkills.sort((a, b) => {
                  const aCore = String(a?.core_or_supporting || '').toLowerCase().startsWith('core') ? 1 : 0;
                  const bCore = String(b?.core_or_supporting || '').toLowerCase().startsWith('core') ? 1 : 0;
                  if (aCore !== bCore) return bCore - aCore; // core first
                  const aRel = typeof a?.relevance_score === 'number' ? a.relevance_score : 0;
                  const bRel = typeof b?.relevance_score === 'number' ? b.relevance_score : 0;
                  return bRel - aRel; // higher relevance first
                });

                return (
                  <CollapsibleCard title={`All Skills Analysis (${sortedSkills.length} skills)`} defaultOpen compact>
                    <div className="overflow-x-auto overflow-y-auto max-h-[420px] border rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proficiency</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Years</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Core/Supporting</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Context</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {sortedSkills.map((skill: any, i: number) => (
                            <tr key={i} className={skill.core_or_supporting?.toLowerCase().startsWith('core') ? 'bg-blue-50' : ''}>
                              <td className="px-3 py-1.5 whitespace-nowrap text-xs font-medium text-gray-900">
                                {skill.skill}
                              </td>
                              <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-700">
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  skill.category === 'Technical' ? 'bg-blue-100 text-blue-800' :
                                  skill.category === 'Programming' ? 'bg-purple-100 text-purple-800' :
                                  skill.category === 'Frameworks' ? 'bg-indigo-100 text-indigo-800' :
                                  skill.category === 'Tools' ? 'bg-gray-100 text-gray-800' :
                                  skill.category === 'Soft Skills' ? 'bg-green-100 text-green-800' :
                                  skill.category === 'Languages' ? 'bg-pink-100 text-pink-800' :
                                  skill.category === 'Methodologies' ? 'bg-orange-100 text-orange-800' :
                                  skill.category === 'Certifications' ? 'bg-yellow-100 text-yellow-800' :
                                  skill.category === 'Emerging' ? 'bg-red-100 text-red-800' :
                                  'bg-teal-100 text-teal-800'
                                }`}>
                                  {skill.category}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-700">
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  skill.proficiency === 'Expert' ? 'bg-green-100 text-green-800' :
                                  skill.proficiency === 'Advanced' ? 'bg-blue-100 text-blue-800' :
                                  skill.proficiency === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                  skill.proficiency === 'Basic' || skill.proficiency === 'Beginner' ? 'bg-gray-100 text-gray-800' :
                                  'bg-gray-50 text-gray-600'
                                }`}>
                                  {skill.proficiency || '-'}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-700">{skill.years_experience || '-'}</td>
                              <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-700">
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  skill.core_or_supporting?.toLowerCase().startsWith('core')
                                    ? 'bg-green-100 text-green-800 font-medium'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {skill.core_or_supporting || '-'}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-xs text-gray-600 max-w-xs truncate" title={skill.context_demonstrated || '-'}>
                                {skill.context_demonstrated || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CollapsibleCard>
                );
              })()}
            </div>
          )}

          {activeTab === 'experience' && (
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              {/* Experience Summary */}
              <CollapsibleCard title="Experience Summary" defaultOpen compact>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 divide-x divide-blue-200">
                    <div className="text-center px-2 py-1">
                      <div className="text-lg font-bold text-blue-600">{resume.analysis?.career_analysis?.total_years_experience || 0}</div>
                      <div className="text-xs text-blue-700">Years Experience</div>
                    </div>
                    <div className="text-center px-2 py-1">
                      <div className="text-lg font-bold text-blue-600">
                        {resume.analysis?.career_analysis?.leadership_experience ? 'Yes' : 'No'}
                      </div>
                      <div className="text-xs text-blue-700">Leadership Experience</div>
                    </div>
                    <div className="text-center px-2 py-1">
                      <div className="text-sm font-bold text-blue-600 truncate" title={resume.analysis?.career_analysis?.current_position || 'N/A'}>
                        {resume.analysis?.career_analysis?.current_position || 'N/A'}
                      </div>
                      <div className="text-xs text-blue-700">Current Position</div>
                    </div>
                    <div className="text-center px-2 py-1">
                      <div className="text-sm font-bold text-blue-600 truncate" title={resume.analysis?.career_analysis?.current_company || 'N/A'}>
                        {resume.analysis?.career_analysis?.current_company || 'N/A'}
                      </div>
                      <div className="text-xs text-blue-700">Current Company</div>
                    </div>
                    {resume.analysis?.career_analysis?.career_progression_score && (
                      <div className="text-center px-2 py-1">
                        <div className="text-lg font-bold text-blue-600">
                          {Math.round((resume.analysis.career_analysis.career_progression_score || 0) * 100)}%
                        </div>
                        <div className="text-xs text-blue-700">Career Progression</div>
                      </div>
                    )}
                  </div>
                </div>
              </CollapsibleCard>

              {/* Work Experience */}
              {resume.analysis?.career_analysis?.work_experience && (
                <CollapsibleCard title="Work Experience" defaultOpen compact>
                  <div className="space-y-3">
                    {resume.analysis.career_analysis.work_experience.map((exp: any, index: number) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        {/* Role/Position and Dates Header */}
                        <div className="flex justify-between items-start mb-1.5">
                          <h4 className="text-sm font-medium text-gray-900">{exp.position}</h4>
                          <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                            {exp.start_date} - {exp.end_date}
                          </span>
                        </div>

                        {/* Client (if exists) */}
                        {exp.client && (
                          <p className="text-xs text-gray-600 mb-1">
                            Client: {exp.client}
                          </p>
                        )}

                        {/* Company (if no client, show company as employer) */}
                        {!exp.client && exp.company && (
                          <p className="text-xs text-gray-600 mb-1">
                            {exp.company}
                          </p>
                        )}

                        {/* Location (if exists) */}
                        {exp.location && (
                          <p className="text-xs text-gray-600 mb-2">
                            Location: {exp.location}
                          </p>
                        )}

                        {/* Description */}
                        {exp.description && (
                          <p className="text-xs text-gray-700 mb-2">{exp.description}</p>
                        )}

                        {/* Key Achievements */}
                        {exp.achievements && exp.achievements.length > 0 && (
                          <div>
                            <h5 className="text-xs font-medium text-gray-900 mb-1.5">Key Achievements:</h5>
                            <ul className="text-xs text-gray-700 space-y-1">
                              {exp.achievements.map((achievement: string, achIndex: number) => (
                                <li key={achIndex} className="flex items-start space-x-2">
                                  <span className="text-gray-400 mt-1">•</span>
                                  <span>{achievement}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CollapsibleCard>
              )}
            </div>
          )}

          {activeTab === 'education' && (
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              {/* Education Summary */}
              <CollapsibleCard title="Education Summary" defaultOpen compact>
                <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-green-200">
                    <div className="px-2 py-1">
                      <span className="text-xs text-green-700">Highest Degree:</span>
                      <div className="text-sm font-medium text-green-900">{resume.analysis?.education_analysis?.highest_degree || 'Not specified'}</div>
                    </div>
                    <div className="px-2 py-1">
                      <span className="text-xs text-green-700">Education Relevance:</span>
                      <div className="text-sm font-medium text-green-900">
                        {resume.analysis?.education_analysis?.relevant_education ? 'Highly Relevant' : 'Partially Relevant'}
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleCard>

              {/* Educational Background */}
              {resume.analysis?.education_analysis?.education && (
                <CollapsibleCard title="Educational Background" defaultOpen compact>
                  <div className="space-y-2">
                    {resume.analysis.education_analysis.education.map((edu: any, index: number) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{edu.degree}</h4>
                            <p className="text-xs text-gray-600">{edu.institution}</p>
                            {edu.honors && (
                              <p className="text-xs text-blue-600 font-medium">{edu.honors}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">{edu.graduation_date}</div>
                            {edu.gpa && (
                              <div className="text-xs font-medium text-gray-900">GPA: {edu.gpa}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleCard>
              )}
            </div>
          )}

          {activeTab === 'matching' && (
            <div className="space-y-3">
              {/* Job Search Section */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-1.5">
                  <UserGroupIcon className="w-4 h-4 text-blue-600" />
                  <span>Search Jobs to Apply</span>
                </h3>

                <div className="space-y-3">
                  {/* Search Section */}
                  <div className="flex space-x-2">
                    <div className="flex-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Search jobs by title or id..."
                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={handleSearch}
                      disabled={!searchTerm.trim()}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5"
                    >
                      <MagnifyingGlassIcon className="w-3.5 h-3.5" />
                      <span>Search</span>
                    </button>
                    {searchQuery && (
                      <button
                        onClick={handleClearSearch}
                        className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center space-x-1.5"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                        <span>Clear</span>
                      </button>
                    )}
                  </div>

                  {/* Search Results */}
                  {searchQuery && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">
                          {jobsLoading ? 'Searching...' : `${jobs.length} jobs found`}
                        </span>
                        {selectedJobs.length > 0 && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-600">
                              {selectedJobs.length} selected
                            </span>
                            <button
                              onClick={handleCompareJobs}
                              disabled={comparisonLoading}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5"
                            >
                              {comparisonLoading ? (
                                <>
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                                  <span>Processing...</span>
                                </>
                              ) : (
                                <span>Apply Selected</span>
                              )}
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {jobsLoading ? (
                          <div className="flex justify-center py-8">
                            <LoadingSpinner size="md" />
                          </div>
                        ) : jobs.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <BriefcaseIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p>No jobs found matching your search</p>
                            <p className="text-sm">Try different search terms</p>
                          </div>
                        ) : (
                          jobs.map((job: any) => {
                            const isAlreadyAnalyzed = alreadyAnalyzedJobs.includes(job.id)
                            const isSelected = selectedJobs.includes(job.id)
                            const isDisabled = isAlreadyAnalyzed  // Don't disable if just selected, allow unselection
                            
                            return (
                              <div
                                key={job.id}
                                className={`p-3 border rounded-lg transition-colors ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50'
                                    : isAlreadyAnalyzed
                                    ? 'border-gray-200 bg-gray-50 opacity-60'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div 
                                    className={`flex items-center space-x-3 flex-1 ${
                                      isAlreadyAnalyzed ? 'cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                    onClick={() => {
                                      if (!isAlreadyAnalyzed) {
                                        handleJobSelect(job.id)
                                      }
                                    }}
                                  >
                                    <div className={`w-4 h-4 border-2 rounded ${
                                      isSelected
                                        ? 'border-blue-500 bg-blue-500'
                                        : isAlreadyAnalyzed
                                        ? 'border-gray-300 bg-gray-200'
                                        : 'border-gray-300'
                                    }`}>
                                      {isSelected && (
                                        <CheckIcon className="w-3 h-3 text-white" />
                                      )}
                                      {isAlreadyAnalyzed && (
                                        <XMarkIcon className="w-3 h-3 text-gray-500" />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2">
                                        {job.job_id && (
                                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-mono">
                                            {job.job_id}
                                          </span>
                                        )}
                                        <h4 className={`font-medium ${
                                          isDisabled ? 'text-gray-500' : 'text-gray-900'
                                        }`}>
                                          {job.title}
                                        </h4>
                                        {isAlreadyAnalyzed && (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                            <CheckIcon className="w-3 h-3 mr-1" />
                                            Already Selected
                                          </span>
                                        )}
                                        {isSelected && !isAlreadyAnalyzed && (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                                            <CheckIcon className="w-3 h-3 mr-1" />
                                            Selected
                                          </span>
                                        )}
                                      </div>
                                      <div className={`text-sm space-y-1 ${
                                        isDisabled ? 'text-gray-400' : 'text-gray-600'
                                      }`}>
                                        {job.department && <div className="flex items-center space-x-1">
                                          <BuildingOfficeIcon className="w-4 h-4" />
                                          <span className="font-medium">{job.department}</span>
                                        </div>}
                                        {job.location && <div className="flex items-center space-x-1">
                                          <MapPinIcon className="w-4 h-4 text-blue-600" />
                                          <span className="text-blue-600 font-medium">{job.location}</span>
                                        </div>}
                                        {job.employment_type && <div className="flex items-center space-x-1">
                                          <ClockIcon className="w-4 h-4 text-green-600" />
                                          <span className="text-green-600 font-medium">{job.employment_type}</span>
                                        </div>}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Right side: Salary and View Button */}
                                  <div className="flex items-center space-x-4">
                                    {(job.salary_min || job.salary_max) && (
                                      <div className={`text-sm ${
                                        isDisabled ? 'text-gray-400' : 'text-gray-500'
                                      }`}>
                                        {job.salary_min && job.salary_max 
                                          ? `${job.salary_min}-${job.salary_max} ${job.salary_currency}`
                                          : job.salary_min 
                                          ? `${job.salary_min}+ ${job.salary_currency}`
                                          : `Up to ${job.salary_max} ${job.salary_currency}`
                                        }
                                      </div>
                                    )}
                                    
                                    {/* View Job Button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation() // Prevent triggering checkbox
                                        handleViewJobProfile(job.id)
                                      }}
                                      className="px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center space-x-1 bg-blue-600 text-white hover:bg-blue-700"
                                    >
                                      <EyeIcon className="w-4 h-4" />
                                      <span>View</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Analysis Results - Always Visible */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900">Analysis Results</h3>
                </div>

                {/* First load only (isLoading) -- background refetches after a
                    status change keep the table visible instead of flashing this */}
                {jobMatchesLoading ? (
                  <div className="flex flex-col items-center justify-center py-10">
                    <LoadingSpinner size="md" />
                    <p className="mt-3 text-sm text-gray-500">Loading job applications...</p>
                  </div>
                ) : (
                <>
                {/* Summary Insights */}
                {comparisonResults?.matching_insights ? (
                  <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-900 mb-1.5">Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-blue-700 font-medium">Total Jobs Applied:</span>
                        <span className="ml-2">{comparisonResults.total_jobs_analyzed || 0}</span>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Source:</span>
                        <span className="ml-2">{comparisonResults.matching_insights.source || 'database'}</span>
                      </div>
                    </div>
                    {stageBreakdown.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                        <span className="text-blue-700 font-medium mr-1">By Stage:</span>
                        {stageBreakdown.map(([stageName, count]) => (
                          <span
                            key={stageName}
                            className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-blue-200 text-gray-700"
                          >
                            {stageName}
                            <span className="ml-1 font-semibold text-blue-700">{count}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-1.5">Summary</h4>
                    <p className="text-xs text-gray-600">No comparison data available. Select jobs and run comparison to see analysis results.</p>
                  </div>
                )}

                {/* Filter Panel */}
                {comparisonResults?.job_matches && comparisonResults.job_matches.length > 0 && (
                  <FilterPanel
                    filterState={filterState}
                    uniqueUsers={uniqueUsers}
                    uniqueStatuses={uniqueStatuses}
                    onAssignedUserChange={(userId) => setFilterState(prev => ({ ...prev, assignedUserId: userId }))}
                    onStatusChange={(status) => setFilterState(prev => ({ ...prev, status }))}
                    onScoreRangeChange={(range) => setFilterState(prev => ({ ...prev, scoreRange: range }))}
                    onTogglePanel={togglePanel}
                    onClearFilters={clearFilters}
                    activeFilterCount={activeFilterCount}
                  />
                )}

                {/* Result Count Display */}
                {comparisonResults?.job_matches && comparisonResults.job_matches.length > 0 && (
                  <ResultCountDisplay
                    totalCount={comparisonResults.job_matches.length}
                    filteredCount={filteredMatches.length}
                    entityType="jobs"
                    hasActiveFilters={hasFilters}
                  />
                )}

                {/* Comparison Results Cards */}
                <div className="bg-white shadow rounded-lg overflow-visible">
                  {/* Read-only view of every JD this candidate applied to.
                      Horizontal scroll only below lg -- at lg+ the 6 columns fit,
                      and overflow-visible keeps the (i) tooltips from clipping. */}
                  <div className="overflow-x-auto lg:overflow-visible">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">JD Match Score</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interview Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                      {filteredMatches.length > 0 ? (
                        paginatedMatches.map((result: any) => {
                          const skillsBreakdown = result.detailed_analysis && result.detailed_analysis.skills_breakdown
                          const hasSkillsCount = skillsBreakdown &&
                            skillsBreakdown.skills_matched_count !== undefined &&
                            skillsBreakdown.total_required_skills !== undefined
                          const stageColor = getStageColor(result.stage_code)
                          const oldStatus = (result.application_status || '').toUpperCase()
                          const candidateStatusLabel = result.status_name
                            ? result.status_name
                            : oldStatus && oldStatus !== 'MATCHED'
                            ? `${prettifyStatusCode(oldStatus)} (old)`
                            : 'Not Set'
                          return (
                            <tr key={result.job_id} className="hover:bg-gray-50 align-top">
                              {/* Job -- (i) shows when the candidate was applied */}
                              <td className="px-4 py-3 text-sm min-w-[200px]">
                                <div className="flex items-center space-x-1.5">
                                  <span className="font-semibold text-gray-900">{result.job_title}</span>
                                  {result.match_created_at && (
                                    <Tooltip
                                      content={`Applied on ${formatToLocalDateTime(result.match_created_at)}`}
                                      position="bottom"
                                      theme="light"
                                      width="max-content"
                                    >
                                      <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-blue-600 cursor-help" />
                                    </Tooltip>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {[result.job_display_id, result.location].filter(Boolean).join(' • ') || 'N/A'}
                                </div>
                              </td>

                              {/* Job Status */}
                              <td className="px-4 py-3 text-sm whitespace-nowrap">
                                {result.job_status ? (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${JOB_STATUS_BADGE_CLASSES[result.job_status] || 'bg-gray-100 text-gray-700'}`}>
                                    {result.job_status}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>

                              {/* JD Match Score */}
                              <td className="px-4 py-3 text-sm whitespace-nowrap">
                                {result.status !== 'completed' ? (
                                  <MatchingStatusBadge
                                    status={result.status === 'processing' ? 'processing' :
                                           result.status === 'pending' ? 'pending' :
                                           result.status === 'failed' ? 'failed' : 'unknown'}
                                    retryCount={result.retry_count}
                                  />
                                ) : (
                                  <>
                                    {/* gate_status comes from the same TalentOS-
                                        derived job_matches row match_score does
                                        -- an 'ineligible' candidate was never
                                        scored, so "0%" would read as "scored,
                                        and scored terribly" rather than "below
                                        the experience minimum". */}
                                    {result.detailed_analysis && result.detailed_analysis.gate_status === 'ineligible' ? (
                                      <span className="text-sm font-semibold text-gray-400">Not Eligible</span>
                                    ) : (
                                      <span className="text-base font-bold text-blue-600">
                                        {toDisplayPercent(result.match_score || 0)}%
                                      </span>
                                    )}
                                    {hasSkillsCount && (
                                      <div className="text-xs text-gray-500">
                                        Skills {skillsBreakdown.skills_matched_count}/{skillsBreakdown.total_required_skills}
                                      </div>
                                    )}
                                  </>
                                )}
                              </td>

                              {/* Candidate Status -- read-only; (i) shows when and by whom */}
                              <td className="px-4 py-3 text-sm whitespace-nowrap">
                                <div className="flex items-center space-x-1.5">
                                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg border text-xs font-medium ${stageColor.bg} ${stageColor.text} ${stageColor.border}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${stageColor.dot}`} />
                                    {candidateStatusLabel}
                                  </span>
                                  {result.status_updated_at && (
                                    <Tooltip
                                      content={
                                        <div>
                                          <div>{formatToLocalDateTime(result.status_updated_at)}</div>
                                          {result.status_updated_by_name && (
                                            <div className="text-gray-500">by {result.status_updated_by_name}</div>
                                          )}
                                        </div>
                                      }
                                      position="bottom"
                                      theme="light"
                                      width="max-content"
                                    >
                                      <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-blue-600 cursor-help" />
                                    </Tooltip>
                                  )}
                                </div>
                              </td>

                              {/* Interview Status -- latest proctored + live session for this JD */}
                              <td className="px-4 py-3 text-sm whitespace-nowrap">
                                <div className="space-y-1.5">
                                  <InterviewStatusLine label="Proctored" session={result.proctored_interview} kind="proctored" />
                                  <InterviewStatusLine label="Live" session={result.live_interview} kind="live" />
                                </div>
                              </td>

                              {/* Assigned To -- initials avatar, full name on hover */}
                              <td className="px-4 py-3 text-sm whitespace-nowrap">
                                {result.matched_by_name ? (
                                  <div
                                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-[10px] font-semibold cursor-default ${getColor(result.matched_by_name)}`}
                                    title={result.matched_by_name}
                                  >
                                    {getInitials(result.matched_by_name)}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          )
                        })
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                              <BriefcaseIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                              <p>No job matches found</p>
                              <p className="text-sm">Search and select jobs to see analysis results</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Controls */}
                  {filteredMatches.length > itemsPerPage && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
                      <div className="text-sm text-gray-700">
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredMatches.length)} of {filteredMatches.length} results
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-700">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </>
                )}
              </div>
            </div>
          )}

          {/* Interview tab — hidden per request (tab entry commented out
              above; content commented out here too, since it was always
              mounted regardless of activeTab, not conditionally rendered)*/}
          {/* <div className={activeTab === 'interview' ? '' : 'hidden'}>
            <InterviewTab
              entityType="resume"
              entityId={id!}
              candidateName={resume?.candidate_name}
              isAdmin={canDeleteInterview}
              canManageContent={canManageInterviewContent}
              canCreateInterview={canCreateInterview}
              isActive={activeTab === 'interview'}
            />
          </div> */}
          

          {activeTab === 'agents' && (
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              {/* Agent Analysis Header */}
              {/* <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center space-x-2">
                  <BeakerIcon className="w-6 h-6 text-blue-600" />
                  <span>AI Agent Analysis</span>
                </h3>
                <p className="text-gray-600">
                  Raw analysis outputs from specialized AI agents. This provides complete transparency into how each agent analyzed different aspects of the resume.
                </p>
              </div> */}

              {/* Agent Outputs Grid */}
              <div className="grid grid-cols-1 gap-3">
                {/* Personal Information Agent */}
                {resume.agent_personal_info_markdown && (
                  <CollapsibleCard
                    title="Personal Information"
                    subtitle="Extracts contact details and personal information"
                    icon={<UserIcon className="w-4 h-4 text-blue-600" />}
                    defaultOpen={false}
                    headerClassName="bg-blue-50"
                    compact
                  >
                    <div className="bg-gray-50 rounded-lg p-3 overflow-x-auto">
                      <MarkdownRenderer
                        content={resume.agent_personal_info_markdown}
                        className="text-sm"
                      />
                    </div>
                  </CollapsibleCard>
                )}

                {/* Experience Agent */}
                {resume.agent_experience_markdown && (
                  <CollapsibleCard
                    title="Experience Analysis"
                    subtitle="Analyzes work history, career progression, and achievements"
                    icon={<BriefcaseIcon className="w-4 h-4 text-green-600" />}
                    headerClassName="bg-green-50"
                    compact
                  >
                    <div className="bg-gray-50 rounded-lg p-3 overflow-x-auto">
                      <MarkdownRenderer
                        content={resume.agent_experience_markdown}
                        className="text-sm"
                      />
                    </div>
                  </CollapsibleCard>
                )}

                {/* Skills Agent */}
                {resume.agent_skills_markdown && (
                  <CollapsibleCard
                    title="Skills Analysis"
                    subtitle="Identifies and categorizes technical and soft skills with proficiency levels"
                    icon={<CpuChipIcon className="w-4 h-4 text-purple-600" />}
                    headerClassName="bg-purple-50"
                    compact
                  >
                    <div className="bg-gray-50 rounded-lg p-3 overflow-x-auto">
                      <MarkdownRenderer
                        content={resume.agent_skills_markdown}
                        className="text-sm"
                      />
                    </div>
                  </CollapsibleCard>
                )}

                {/* Education Agent */}
                {resume.agent_education_markdown && (
                  <CollapsibleCard
                    title="Education Analysis"
                    subtitle="Analyzes educational background, degrees, and certifications"
                    icon={<AcademicCapIcon className="w-4 h-4 text-indigo-600" />}
                    headerClassName="bg-indigo-50"
                    compact
                  >
                    <div className="bg-gray-50 rounded-lg p-3 overflow-x-auto">
                      <MarkdownRenderer
                        content={resume.agent_education_markdown}
                        className="text-sm"
                      />
                    </div>
                  </CollapsibleCard>
                )}

                {/* Quality Assessment Agent */}
                {resume.agent_quality_markdown && (
                  <CollapsibleCard
                    title="Quality Assessment"
                    subtitle="Evaluates resume quality, completeness, and presentation"
                    icon={<ChartBarIcon className="w-4 h-4 text-yellow-600" />}
                    headerClassName="bg-yellow-50"
                    compact
                  >
                    <div className="bg-gray-50 rounded-lg p-3 overflow-x-auto">
                      <MarkdownRenderer
                        content={resume.agent_quality_markdown}
                        className="text-sm"
                      />
                    </div>
                  </CollapsibleCard>
                )}

                {/* Reasoning Agent */}
                {/* Reasoning Agent - COMMENTED OUT */}
                {/*
                {resume.agent_reasoning_markdown && (
                  <CollapsibleCard
                    title="Reasoning"
                    subtitle="Provides comprehensive reasoning and hiring recommendations"
                    icon={<BeakerIcon className="w-5 h-5 text-red-600" />}
                    headerClassName="bg-red-50"
                  >
                    <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
                      <MarkdownRenderer 
                        content={resume.agent_reasoning_markdown}
                        className="text-sm"
                      />
                    </div>
                  </CollapsibleCard>
                )}
                */}

                {/* Raw Agent Outputs (for debugging) */}
                {/* {resume.agent_raw_outputs && Object.keys(resume.agent_raw_outputs).length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <CodeBracketIcon className="w-5 h-5" />
                        <span>Raw Agent Outputs (Debug)</span>
                      </h4>
                      <p className="text-sm text-gray-700 mt-1">Complete raw outputs from all agents for debugging purposes</p>
                    </div>
                    <div className="p-6">
                      <div className="bg-gray-900 rounded-lg p-4 text-sm overflow-x-auto">
                        <pre className="text-green-400 whitespace-pre-wrap">
                          {JSON.stringify(resume.agent_raw_outputs, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                )} */}


                {/* AI Analysis Fallback - Show structured data when markdown not available */}
                {resume.analysis && (
                  <div className="space-y-3">
                    {/* Key Strengths, Areas for Improvement, Recommendations, and AI Reasoning - Top Priority */}
                    {(resume.analysis.quality_assessment?.strengths?.length > 0 ||
                      resume.analysis.quality_assessment?.improvement_areas?.length > 0 ||
                      resume.analysis.quality_assessment?.recommendations?.length > 0 ||
                      resume.analysis.quality_assessment?.ai_reasoning) && (
                      <div className="space-y-3">
                        {/* Key Strengths */}
                        {resume.analysis.quality_assessment.strengths && resume.analysis.quality_assessment.strengths.length > 0 && (
                          <CollapsibleCard title="Key Strengths" icon={<CheckCircleIcon className="w-4 h-4 text-green-500" />} defaultOpen compact>
                            <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                              <ul className="space-y-1.5">
                                {resume.analysis.quality_assessment.strengths.map((strength: string, index: number) => (
                                  <li key={index} className="flex items-start space-x-2">
                                    <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-green-800">{strength}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </CollapsibleCard>
                        )}

                        {/* Areas for Improvement */}
                        {resume.analysis.quality_assessment.improvement_areas && resume.analysis.quality_assessment.improvement_areas.length > 0 && (
                          <CollapsibleCard title="Areas for Improvement" icon={<ExclamationTriangleIcon className="w-4 h-4 text-orange-500" />} defaultOpen compact>
                            <div className="bg-orange-50 border border-orange-100 rounded-lg p-3">
                              <ul className="space-y-1.5">
                                {resume.analysis.quality_assessment.improvement_areas.map((area: string, index: number) => (
                                  <li key={index} className="flex items-start space-x-2">
                                    <ExclamationTriangleIcon className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-orange-800">{area}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </CollapsibleCard>
                        )}

                        {/* Recommendations */}
                        {resume.analysis.quality_assessment.recommendations && resume.analysis.quality_assessment.recommendations.length > 0 && (
                          <CollapsibleCard title="Recommendations" icon={<CheckCircleIcon className="w-4 h-4 text-blue-500" />} defaultOpen compact>
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                              <ul className="space-y-1.5">
                                {resume.analysis.quality_assessment.recommendations.map((rec: string, index: number) => (
                                  <li key={index} className="flex items-start space-x-2">
                                    <CheckCircleIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-blue-800">{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </CollapsibleCard>
                        )}

                        {/* AI Analysis Reasoning */}
                        {resume.analysis.quality_assessment.ai_reasoning && (
                          <CollapsibleCard title="AI Analysis Reasoning" icon={<BeakerIcon className="w-4 h-4 text-purple-500" />} defaultOpen compact>
                            <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                              <p className="text-sm text-purple-800 leading-relaxed">
                                {resume.analysis.quality_assessment.ai_reasoning}
                              </p>
                            </div>
                          </CollapsibleCard>
                        )}
                      </div>
                    )}

                    {/* Personal Information Analysis */}
                    {resume.analysis.personal_info && (
                      <CollapsibleCard
                        title="Personal Information Analysis"
                        subtitle="AI-extracted personal details and contact information"
                        icon={<UserIcon className="w-4 h-4 text-blue-600" />}
                        defaultOpen={true}
                        headerClassName="bg-blue-50"
                        compact
                      >
                        <div className="bg-white rounded-lg p-3 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {resume.analysis.personal_info.full_name && (
                              <div className="flex items-center space-x-2">
                                <UserIcon className="w-4 h-4 text-blue-500" />
                                <div>
                                  <p className="text-xs font-medium text-gray-900">Full Name</p>
                                  <p className="text-xs text-gray-600">{resume.analysis.personal_info.full_name}</p>
                                </div>
                              </div>
                            )}
                            {resume.analysis.personal_info.email && (
                              <div className="flex items-center space-x-2">
                                <EnvelopeIcon className="w-4 h-4 text-blue-500" />
                                <div>
                                  <p className="text-xs font-medium text-gray-900">Email</p>
                                  <p className="text-xs text-gray-600">{resume.analysis.personal_info.email}</p>
                                </div>
                              </div>
                            )}
                            {resume.analysis.personal_info.phone && (
                              <div className="flex items-center space-x-2">
                                <PhoneIcon className="w-4 h-4 text-blue-500" />
                                <div>
                                  <p className="text-xs font-medium text-gray-900">Phone</p>
                                  <p className="text-xs text-gray-600">{formatPhoneNumber(resume.analysis.personal_info.phone)}</p>
                                </div>
                              </div>
                            )}
                            {resume.analysis.personal_info.location && (
                              <div className="flex items-center space-x-2">
                                <MapPinIcon className="w-4 h-4 text-blue-500" />
                                <div>
                                  <p className="text-xs font-medium text-gray-900">Location</p>
                                  <p className="text-xs text-gray-600">{resume.analysis.personal_info.location}</p>
                                </div>
                              </div>
                            )}
                            {resume.analysis.personal_info.professional_title && (
                              <div className="flex items-center space-x-2 md:col-span-2">
                                <BriefcaseIcon className="w-4 h-4 text-blue-500" />
                                <div>
                                  <p className="text-xs font-medium text-gray-900">Professional Title</p>
                                  <p className="text-xs text-gray-600">{resume.analysis.personal_info.professional_title}</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Social Links */}
                          {(resume.analysis.personal_info.linkedin_url || resume.analysis.personal_info.github_url || resume.analysis.personal_info.portfolio_url) && (
                            <div className="border-t pt-3">
                              <h4 className="text-xs font-medium text-gray-900 mb-2">Social Links</h4>
                              <div className="flex flex-wrap gap-3">
                                {resume.analysis.personal_info.linkedin_url && (
                                  <a href={resume.analysis.personal_info.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1.5 text-blue-600 hover:text-blue-800">
                                    <span className="text-xs">LinkedIn</span>
                                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                  </a>
                                )}
                                {resume.analysis.personal_info.github_url && (
                                  <a href={resume.analysis.personal_info.github_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1.5 text-gray-600 hover:text-gray-800">
                                    <span className="text-xs">GitHub</span>
                                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                  </a>
                                )}
                                {resume.analysis.personal_info.portfolio_url && (
                                  <a href={resume.analysis.personal_info.portfolio_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1.5 text-purple-600 hover:text-purple-800">
                                    <span className="text-xs">Portfolio</span>
                                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                  </a>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleCard>
                    )}

                    {/* Skills Analysis */}
                    {resume.analysis.skills_analysis && (
                      <CollapsibleCard
                        title="Skills Analysis"
                        subtitle="AI-powered skills extraction and categorization"
                        icon={<CpuChipIcon className="w-4 h-4 text-purple-600" />}
                        defaultOpen={false}
                        headerClassName="bg-purple-50"
                        compact
                      >
                        <div className="bg-white rounded-lg p-3 space-y-3">
                          {/* Technical Skills */}
                          {resume.analysis.skills_analysis.technical_skills && resume.analysis.skills_analysis.technical_skills.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                <CpuChipIcon className="w-4 h-4 text-purple-500 mr-1.5" />
                                Technical Skills
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {resume.analysis.skills_analysis.technical_skills.map((skill: any, index: number) => {
                                  // TalentOS-engine résumés supply plain skill-name strings here
                                  // (talentos/projections/resume_projection_pure.py), not Foundry's
                                  // richer {skill, proficiency, years_experience, context_demonstrated}
                                  // object -- TalentOS genuinely has no per-skill proficiency rating to
                                  // show, so the badge is simply omitted rather than showing a fake
                                  // "Unknown" for every skill (bug fix 2026-10-15).
                                  const isPlainString = typeof skill === 'string'
                                  const skillName = isPlainString ? skill : skill.skill
                                  return (
                                    <div key={index} className="bg-purple-50 rounded-lg p-2.5">
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900">{skillName}</span>
                                        {!isPlainString && (
                                          <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">
                                            {skill.proficiency || 'Unknown'}
                                          </span>
                                        )}
                                      </div>
                                      {!isPlainString && skill.years_experience && (
                                        <p className="text-xs text-gray-600 mt-1">{skill.years_experience} years experience</p>
                                      )}
                                      {!isPlainString && skill.context_demonstrated && (
                                        <p className="text-xs text-gray-500 mt-1">{skill.context_demonstrated}</p>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {/* Soft Skills */}
                          {resume.analysis.skills_analysis.soft_skills && resume.analysis.skills_analysis.soft_skills.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                <UserGroupIcon className="w-4 h-4 text-green-500 mr-1.5" />
                                Soft Skills
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {resume.analysis.skills_analysis.soft_skills.map((skill: any, index: number) => {
                                  // Same TalentOS-string-vs-Foundry-object distinction as
                                  // Technical Skills above.
                                  const isPlainString = typeof skill === 'string'
                                  const skillName = isPlainString ? skill : skill.skill
                                  return (
                                    <div key={index} className="bg-green-50 rounded-lg p-2.5">
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900">{skillName}</span>
                                        {!isPlainString && (
                                          <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                                            {skill.proficiency || 'Unknown'}
                                          </span>
                                        )}
                                      </div>
                                      {!isPlainString && skill.years_experience && (
                                        <p className="text-xs text-gray-600 mt-1">{skill.years_experience} years experience</p>
                                      )}
                                      {!isPlainString && skill.context_demonstrated && (
                                        <p className="text-xs text-gray-500 mt-1">{skill.context_demonstrated}</p>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {/* Methodologies */}
                          {resume.analysis.skills_analysis.methodologies && resume.analysis.skills_analysis.methodologies.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                <CogIcon className="w-4 h-4 text-blue-500 mr-1.5" />
                                Methodologies
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {resume.analysis.skills_analysis.methodologies.map((skill: any, index: number) => (
                                  <span key={index} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium">
                                    {skill.skill}
                                    {skill.proficiency && (
                                      <span className="ml-2 text-xs bg-blue-200 px-2 py-0.5 rounded-full">
                                        {skill.proficiency}
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Tools & Technologies */}
                          {resume.analysis.skills_analysis.tools_technologies && resume.analysis.skills_analysis.tools_technologies.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                <WrenchScrewdriverIcon className="w-4 h-4 text-orange-500 mr-1.5" />
                                Tools & Technologies
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {resume.analysis.skills_analysis.tools_technologies.map((skill: any, index: number) => (
                                  <span key={index} className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full text-xs font-medium">
                                    {skill.skill}
                                    {skill.proficiency && (
                                      <span className="ml-2 text-xs bg-orange-200 px-2 py-0.5 rounded-full">
                                        {skill.proficiency}
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleCard>
                    )}

                    {/* Career Analysis */}
                    {resume.analysis.career_analysis && (
                      <CollapsibleCard
                        title="Career Analysis"
                        subtitle="Work experience and career progression analysis"
                        icon={<BriefcaseIcon className="w-4 h-4 text-green-600" />}
                        defaultOpen={false}
                        headerClassName="bg-green-50"
                        compact
                      >
                        <div className="bg-white rounded-lg p-3 space-y-3">
                          {/* Career Summary */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="bg-green-50 rounded-lg p-3">
                              <div className="flex items-center space-x-1.5 mb-1.5">
                                <ClockIcon className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-medium text-gray-900">Total Experience</span>
                              </div>
                              <p className="text-lg font-bold text-green-600">
                                {resume.analysis.career_analysis.total_years_experience || 0} years
                              </p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3">
                              <div className="flex items-center space-x-1.5 mb-1.5">
                                <BriefcaseIcon className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-medium text-gray-900">Current Position</span>
                              </div>
                              <p className="text-xs text-gray-700">{resume.analysis.career_analysis.current_position || 'Not specified'}</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-3">
                              <div className="flex items-center space-x-1.5 mb-1.5">
                                <BuildingOfficeIcon className="w-4 h-4 text-purple-600" />
                                <span className="text-sm font-medium text-gray-900">Current Company</span>
                              </div>
                              <p className="text-xs text-gray-700">{resume.analysis.career_analysis.current_company || 'Not specified'}</p>
                            </div>
                          </div>

                          {/* Leadership Experience */}
                          {resume.analysis.career_analysis.leadership_experience && (
                            <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3">
                              <div className="flex items-center space-x-2">
                                <UserGroupIcon className="w-4 h-4 text-yellow-600" />
                                <span className="text-sm font-medium text-yellow-900">Leadership Experience</span>
                                <span className="bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-medium">Yes</span>
                              </div>
                            </div>
                          )}

                          {/* Career Progression Score */}
                          {resume.analysis.career_analysis.career_progression_score && (
                            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-1.5">
                                  <ChartBarIcon className="w-4 h-4 text-indigo-600" />
                                  <span className="text-sm font-medium text-indigo-900">Career Progression Score</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <div className="w-24 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-indigo-600 h-2 rounded-full"
                                      style={{ width: `${(resume.analysis.career_analysis.career_progression_score * 100)}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs font-medium text-indigo-900">
                                    {Math.round(resume.analysis.career_analysis.career_progression_score * 100)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Work Experience */}
                          {resume.analysis.career_analysis.work_experience && resume.analysis.career_analysis.work_experience.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                <BriefcaseIcon className="w-4 h-4 text-green-500 mr-1.5" />
                                Work Experience
                              </h4>
                              <div className="space-y-2">
                                {resume.analysis.career_analysis.work_experience.map((job: any, index: number) => (
                                  <div key={index} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                    {/* Role/Position and Dates Header */}
                                    <div className="flex items-start justify-between mb-1.5">
                                      <h5 className="text-sm font-semibold text-gray-900">{job.position}</h5>
                                      <div className="text-right">
                                        <p className="text-xs font-medium text-gray-900 whitespace-nowrap ml-4">{job.start_date} - {job.end_date}</p>
                                        {job.duration && (
                                          <p className="text-xs text-gray-500">{job.duration}</p>
                                        )}
                                      </div>
                                    </div>

                                    {/* Client (if exists) */}
                                    {job.client && (
                                      <p className="text-xs text-gray-600 mb-1">
                                        Client: {job.client}
                                      </p>
                                    )}

                                    {/* Company (if no client, show company as employer) */}
                                    {!job.client && job.company && (
                                      <p className="text-xs text-gray-600 mb-1">
                                        {job.company}
                                      </p>
                                    )}

                                    {/* Location (if exists) */}
                                    {job.location && (
                                      <p className="text-xs text-gray-600 mb-1">
                                        Location: {job.location}
                                      </p>
                                    )}

                                    {job.responsibilities && job.responsibilities.length > 0 && (
                                      <div className="mt-2">
                                        <h6 className="text-xs font-medium text-gray-700 mb-1.5">Key Responsibilities:</h6>
                                        <ul className="text-xs text-gray-600 space-y-1">
                                          {job.responsibilities.map((resp: string, respIndex: number) => (
                                            <li key={respIndex} className="flex items-start space-x-2">
                                              <span className="text-green-500 mt-1">•</span>
                                              <span>{resp}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {job.achievements && job.achievements.length > 0 && (
                                      <div className="mt-2">
                                        <h6 className="text-xs font-medium text-gray-700 mb-1.5">Key Achievements:</h6>
                                        <ul className="text-xs text-gray-600 space-y-1">
                                          {job.achievements.map((achievement: string, achIndex: number) => (
                                            <li key={achIndex} className="flex items-start space-x-2">
                                              <span className="text-blue-500 mt-1">•</span>
                                              <span>{achievement}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {job.technologies_used && job.technologies_used.length > 0 && (
                                      <div className="mt-2">
                                        <h6 className="text-xs font-medium text-gray-700 mb-1.5">Technologies Used:</h6>
                                        <div className="flex flex-wrap gap-1">
                                          {job.technologies_used.map((tech: string, techIndex: number) => (
                                            <span key={techIndex} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs">
                                              {tech}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleCard>
                    )}

                    {/* Education Analysis */}
                    {resume.analysis.education_analysis && (
                      <CollapsibleCard
                        title="Education Analysis"
                        subtitle="Educational background and qualifications"
                        icon={<AcademicCapIcon className="w-4 h-4 text-indigo-600" />}
                        defaultOpen={false}
                        headerClassName="bg-indigo-50"
                        compact
                      >
                        <div className="bg-white rounded-lg p-3 space-y-3">
                          {/* Education Summary */}
                          {resume.analysis.education_analysis.highest_degree && (
                            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                              <div className="flex items-center space-x-2">
                                <AcademicCapIcon className="w-4 h-4 text-indigo-600" />
                                <span className="text-sm font-medium text-indigo-900">Highest Degree</span>
                                <span className="bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full text-xs font-medium">
                                  {resume.analysis.education_analysis.highest_degree}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Education Details */}
                          {resume.analysis.education_analysis.education && resume.analysis.education_analysis.education.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                <AcademicCapIcon className="w-4 h-4 text-indigo-500 mr-1.5" />
                                Education Details
                              </h4>
                              <div className="space-y-2">
                                {resume.analysis.education_analysis.education.map((edu: any, index: number) => {
                                  // TalentOS-engine résumés supply one combined free-text line per
                                  // degree here (e.g. "Bachelor of Science in Computers, Morning Star
                                  // Degree College, Guntur, 2016-2019" -- see
                                  // talentos_resume_analyses.education_summary), not Foundry's
                                  // structured {degree, institution, location, field_of_study,
                                  // graduation_year, gpa, honors} object. TalentOS never broke the
                                  // line apart into those fields, so showing it as one line is
                                  // honest; guessing which part is the institution vs the location
                                  // would not be (bug fix 2026-10-15).
                                  const isPlainString = typeof edu === 'string'
                                  return (
                                    <div key={index} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                      {isPlainString ? (
                                        <p className="text-sm font-semibold text-gray-900">{edu}</p>
                                      ) : (
                                        <>
                                          <div className="flex items-start justify-between mb-1.5">
                                            <div>
                                              <h5 className="text-sm font-semibold text-gray-900">{edu.degree}</h5>
                                              <p className="text-xs text-gray-600">{edu.institution}</p>
                                              <p className="text-xs text-gray-500">{edu.location}</p>
                                              {edu.field_of_study && (
                                                <p className="text-xs text-gray-500">Field: {edu.field_of_study}</p>
                                              )}
                                            </div>
                                            <div className="text-right">
                                              {edu.graduation_year && (
                                                <p className="text-xs font-medium text-gray-900">{edu.graduation_year}</p>
                                              )}
                                              {edu.gpa && (
                                                <p className="text-xs text-gray-500">GPA: {edu.gpa}</p>
                                              )}
                                            </div>
                                          </div>
                                          {edu.honors && (
                                            <div className="mt-1.5">
                                              <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs font-medium">
                                                {edu.honors}
                                              </span>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {/* Certifications */}
                          {resume.analysis.education_analysis.certifications && resume.analysis.education_analysis.certifications.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                <DocumentTextIcon className="w-4 h-4 text-green-500 mr-1.5" />
                                Certifications
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {resume.analysis.education_analysis.certifications.map((cert: any, index: number) => (
                                  <div key={index} className="bg-green-50 border border-green-100 rounded-lg p-2.5">
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <h6 className="text-sm font-medium text-gray-900">{cert.name}</h6>
                                        {cert.issuer && (
                                          <p className="text-xs text-gray-600">{cert.issuer}</p>
                                        )}
                                        {cert.date_earned && (
                                          <p className="text-xs text-gray-500">Earned: {cert.date_earned}</p>
                                        )}
                                        {cert.expiration_date && (
                                          <p className="text-xs text-gray-500">Expires: {cert.expiration_date}</p>
                                        )}
                                      </div>
                                      {cert.credential_id && (
                                        <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded text-xs font-mono">
                                          {cert.credential_id}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleCard>
                    )}

                    {/* Quality Assessment */}
                    {resume.analysis.quality_assessment && (
                      <CollapsibleCard
                        title="Quality Assessment"
                        subtitle="Resume quality metrics and evaluation"
                        icon={<ChartBarIcon className="w-4 h-4 text-yellow-600" />}
                        defaultOpen={false}
                        headerClassName="bg-yellow-50"
                        compact
                      >
                        <div className="bg-white rounded-lg p-3 space-y-3">
                          {/* Overall Score */}
                          <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-1.5">
                                <ChartBarIcon className="w-4 h-4 text-yellow-600" />
                                <span className="text-sm font-medium text-yellow-900">Overall Quality Score</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="w-24 bg-gray-200 rounded-full h-2.5">
                                  <div
                                    className="bg-yellow-600 h-2.5 rounded-full"
                                    style={{ width: `${(resume.analysis.quality_assessment.overall_score || 0) * 100}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-bold text-yellow-900">
                                  {Math.round((resume.analysis.quality_assessment.overall_score || 0) * 100)}%
                                </span>
                              </div>
                            </div>
                            <div className="mt-1.5">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                (resume.analysis.quality_assessment.overall_score || 0) >= 0.8
                                  ? 'bg-green-100 text-green-800'
                                  : (resume.analysis.quality_assessment.overall_score || 0) >= 0.6
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                              }`}>
                                Grade: {resume.analysis.quality_assessment.grade || 'N/A'}
                              </span>
                            </div>
                          </div>

                          {/* Quality Scores Breakdown */}
                          {resume.analysis.quality_assessment.quality_scores && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Quality Breakdown</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {Object.entries(resume.analysis.quality_assessment.quality_scores).map(([key, value]) => (
                                  <div key={key} className="bg-gray-50 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <span className="text-xs font-medium text-gray-700 capitalize">
                                        {key.replace('_', ' ')}
                                      </span>
                                      <span className="text-xs font-bold text-gray-900">
                                        {Math.round((value as number) * 100)}%
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                      <div
                                        className="bg-blue-600 h-1.5 rounded-full"
                                        style={{ width: `${(value as number) * 100}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleCard>
                    )}
                  </div>
                )}

                {/* No Agent Data Message - Only show when there's truly no data */}
                {!resume.analysis && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                    <BeakerIcon className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-yellow-900 mb-2">No Agent Analysis Available</h4>
                    <p className="text-yellow-700">
                      This resume was processed before agent markdown storage was implemented.
                      {can('resumes.reprocess') ? 'Try reprocessing the resume to see detailed agent analysis.' : 'Contact a senior recruiter or above to reprocess this resume.'}
                    </p>
                    {/* Reprocess button — senior_recruiter+ (RBAC_UI_IMPLEMENTATION_PLAN.md §5.6.E) */}
                    {can('resumes.reprocess') && (
                      <button
                        onClick={() => reprocessMutation.mutate()}
                        disabled={reprocessMutation.isPending}
                        className="mt-4 flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 mx-auto"
                        title="Reprocess Resume"
                      >
                        {reprocessMutation.isPending ? <LoadingSpinner size="sm" /> : <ArrowPathIcon className="w-4 h-4" />}
                        <span>Reprocess Resume</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
      {/* Detailed View Modal */}
      {selectedJobForDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Detailed Analysis</h3>
                <button
                  onClick={() => setSelectedJobForDetails(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              {(() => {
                const result = comparisonResults?.job_matches?.find(
                  (r: any) => r.job_id === selectedJobForDetails
                )
                if (!result) return null
                
                
                return (
                  <div className="space-y-6">
                    {/* Job Header */}
                    <div className="border-b border-gray-200 pb-4">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-xl font-semibold text-gray-900">{result.job_title}</h4>
                        {result.matched_by_name && (
                          <Tooltip
                            content={`Applied by: ${result.matched_by_name}`}
                            position="bottom"
                            theme="light"
                          >
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 cursor-pointer transition-colors">
                              <InformationCircleIcon className="w-4 h-4 text-gray-700 hover:text-blue-600" />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{result.location}</p>
                      {result.job_display_id && (
                        <span className="inline-block mt-2 text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-mono">
                          {result.job_display_id}
                        </span>
                      )}
                    </div>

                    {/* Score Breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-green-600">
                          {Math.round((result.skills_score || 0) * 100)}%
                        </div>
                        <div className="text-xs text-gray-500">Skills</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {(result.matched_skills?.length || result.detailed_analysis?.skills_breakdown?.skills_matched_count || 0)}/{((result.matched_skills?.length || 0) + (result.missing_skills?.length || 0) || result.detailed_analysis?.skills_breakdown?.total_required_skills || 0)} matched
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-orange-600">
                          {Math.round((result.experience_score || 0) * 100)}%
                        </div>
                        <div className="text-xs text-gray-500">Experience</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {(() => {
                            const directYears = result.detailed_analysis?.total_years_experience;
                            const careerYears = result.detailed_analysis?.career_analysis?.total_years_experience;
                            const breakdownYears = result.detailed_analysis?.experience_breakdown?.years_experience;
                            
                            // Fallback: Get from resume analysis if not in job match
                            const resumeYears = resume?.analysis?.career_analysis?.total_years_experience;
                            
                            const finalValue = directYears || careerYears || breakdownYears || resumeYears || 'N/A';
                            
                            return `${finalValue} years`;
                          })()}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-purple-600">
                          {Math.round((result.confidence || 0) * 100)}%
                        </div>
                        <div className="text-xs text-gray-500">Education</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {(() => {
                            const degreeMatch = result.detailed_analysis?.education_breakdown?.degree_match;
                            const resumeHighestDegree = resume?.analysis?.education_analysis?.highest_degree;
                            const talentosEducationSummary = result.detailed_analysis?.education_summary?.[0];


                            // If we have degree match info, use it; otherwise fall back to
                            // whichever engine's own education field this résumé actually has
                            // (Foundry's highest_degree, then TalentOS's education_summary).
                            if (degreeMatch !== undefined && degreeMatch !== null) {
                              return degreeMatch ? 'Degree match' : 'No degree info';
                            } else if (resumeHighestDegree) {
                              return `${resumeHighestDegree} degree`;
                            } else if (talentosEducationSummary) {
                              return talentosEducationSummary;
                            } else {
                              return 'Not evaluated';
                            }
                          })()}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-blue-600">
                          {result.detailed_analysis?.gate_status === 'ineligible'
                            ? 'Not Eligible'
                            : `${toDisplayPercent(result.match_score || 0)}%`}
                        </div>
                        <div className="text-xs text-gray-500">Overall</div>
                        <div className="text-xs text-gray-400 mt-1">
                          Weighted average
                        </div>
                      </div>
                    </div>

                    {/* Skills Matching Details */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                        <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                        Skills Matching Details
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h6 className="text-sm font-medium text-green-700 mb-2">
                            Matched Skills ({result.matched_skills?.length || result.detailed_analysis?.skills_breakdown?.matched_skills?.length || 0})
                          </h6>
                          <div className="space-y-1 max-h-60 overflow-y-auto">
                            {(result.matched_skills || result.detailed_analysis?.skills_breakdown?.matched_skills || [])?.map((skill: any, index: number) => (
                              <div key={index} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">{skill.skill_name || skill.skill}</span>
                                <span className="text-green-600 font-medium">
                                  {Math.round((skill.match_score || 0) * 100)}%
                                </span>
                              </div>
                            ))}
                            {(!result.matched_skills && !result.detailed_analysis?.skills_breakdown?.matched_skills?.length) && (
                              <span className="text-gray-500 text-sm">No matched skills found</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <h6 className="text-sm font-medium text-red-700 mb-2">
                            Missing Skills ({result.missing_skills?.length || result.detailed_analysis?.skills_breakdown?.missing_skills?.length || 0})
                          </h6>
                          <div className="space-y-1 max-h-60 overflow-y-auto">
                            {(result.missing_skills || result.detailed_analysis?.skills_breakdown?.missing_skills || [])?.map((skill: any, index: number) => (
                              <div key={index} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">{skill.skill_name || skill.skill}</span>
                                <span className="text-red-600 font-medium">
                                  {Math.round((skill.match_score || 0) * 100)}%
                                </span>
                              </div>
                            ))}
                            {(!result.missing_skills && !result.detailed_analysis?.skills_breakdown?.missing_skills?.length) && (
                              <span className="text-gray-500 text-sm">No missing skills identified</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 text-xs text-gray-600">
                        <strong>Summary:</strong> {(result.matched_skills?.length || result.detailed_analysis?.skills_breakdown?.skills_matched_count || 0)} out of {((result.matched_skills?.length || 0) + (result.missing_skills?.length || 0) || result.detailed_analysis?.skills_breakdown?.total_required_skills || 0)} required skills matched
                      </div>
                    </div>

                    {/* Strengths and Concerns - Side by Side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* Strengths */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h6 className="font-semibold text-green-800 mb-3">Strengths</h6>
                        <ul className="space-y-2">
                          {(result.reasons || []).map((reason: string, index: number) => (
                            <li key={index} className="text-sm text-gray-700">
                              • {reason}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Concerns */}
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h6 className="font-semibold text-red-800 mb-3">Concerns</h6>
                        <ul className="space-y-2">
                          {(result.concerns || []).map((concern: string, index: number) => (
                            <li key={index} className="text-sm text-gray-700">
                              • {concern}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* AI Recommendation */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h6 className="font-semibold text-blue-800 mb-3">AI Recommendation</h6>
                      <div className="text-sm text-gray-700 leading-relaxed">
                        {result.detailed_reasoning || result.onboarding_recommendations?.join(' ') || 'No detailed recommendation available.'}
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Interview Modal */}
        <InterviewModal
          isOpen={showInterviewModal}
          onClose={handleCloseInterviewModal}
          guide={interviewGuide}
          loading={interviewLoading}
          onReprocess={handleReprocessInterview}
          reprocessing={interviewReprocessing}
        />

      {/* Job Detail Popup */}
      {showJobPopup && selectedJobId && (
        <JobPopupModal
          isOpen={showJobPopup}
          onClose={() => {
            setShowJobPopup(false)
            setSelectedJobId(null)
          }}
          jobId={selectedJobId}
        />
      )}

      {/* Assignment Edit Popover */}
      {selectedAssignment && (
        <AssignmentEditPopover
          isOpen={showAssignmentEdit}
          onClose={() => {
            setShowAssignmentEdit(false)
            setSelectedAssignment(null)
          }}
          entityType={selectedAssignment.entityType}
          entityId={selectedAssignment.entityId}
          currentUserId={selectedAssignment.currentUserId}
          currentUserName={selectedAssignment.currentUserName}
          onSave={handleAssignmentSaved}
        />
      )}

      {/* Audit Trail Modal */}
      {selectedAssignment && (
        <AuditTrailModal
          isOpen={showAuditTrail}
          onClose={() => {
            setShowAuditTrail(false)
            setSelectedAssignment(null)
          }}
          entityType={selectedAssignment.entityType}
          entityId={selectedAssignment.entityId}
          entityTitle={selectedAssignment.entityTitle}
        />
      )}

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Resume"
      >
        Are you sure you want to permanently delete this resume and all its analysis data? This action cannot be undone.
      </ConfirmationModal>
    </div>
  )
}

export default ResumeDetailPage
